/**
 * Document Upload Service — processes user-uploaded files for SiteRAG context.
 *
 * Flow:
 * 1. User uploads PDF/DOCX/TXT/CSV files via the wizard UI
 * 2. Text is extracted client-side (PDF via pdf.js, others via FileReader)
 * 3. Gemini summarizes the content and extracts topic tags
 * 4. Document metadata + summary stored in Firestore at users/{uid}/documents/{docId}
 * 5. During report generation, relevant documents are injected into the Gemini prompt
 *
 * Firestore path: users/{uid}/documents/{docId}
 * Text content is stored in Firestore (not Firebase Storage) to avoid Storage dependency.
 * Truncated to 10K chars to stay within Firestore document size limits.
 */

import type { SiteDocument } from '../../types/siteRag';
import type { SiteEntity } from '../../types/site';
import { summarizeDocument } from './driveConnector';
import { classifyDocumentRelevance, CLASSIFIER_VERSION } from './relevanceClassifier';
import { classifyAndMergeDocTags } from '../docClassifier';
import { NO_MACHINE_READABLE_TEXT_MESSAGE } from './extractionStatusPolicy';
import { REPORT_TYPE_REGISTRY, type ReportTypeId } from '../../config/reportTypes';
import { SED_SECTION_IDS } from '../../types/siteEvidence';
import { cleanDocFileName } from '../../lib/docFileName';
import { wrapUntrusted } from '../../lib/prompts/userContentEnvelope'; // R-131-LLM-A
import { EXTRACTED_TEXT_MAX_CHARS, EXTRACTED_TEXT_HARD_MAX_CHARS, PDF_TEXT_PAGE_CAP } from '../../lib/docExtractionLimits';
import { parseEmlToText, isOleCompoundFile } from '../../lib/emailParser';
import { loadAppStore } from '../../lib/appStore/appStorePort';

/**
 * P1.2 (document-intelligence plan) — the body ceiling. Everything the
 * extractors produce is kept up to this many characters; only a longer
 * document is cut (and stamped `truncated`).
 */
const MAX_TEXT_LENGTH = EXTRACTED_TEXT_HARD_MAX_CHARS;
/** The prompt PREVIEW budget (`SiteDocument.textContent`). */
const PREVIEW_TEXT_LENGTH = EXTRACTED_TEXT_MAX_CHARS;

/**
 * Result of one text extraction BEFORE it is stored. `fullText` is the body
 * up to the ceiling; `text` is the preview (`PREVIEW_TEXT_LENGTH`);
 * `totalChars` is what the extractor produced (0 for bracketed placeholders,
 * which are not extracted content); `truncated` says whether the ceiling
 * cut anything. Surfaces as `SiteDocument.extractedCharsTotal` /
 * `extractedCharsKept` / `truncated` → the Documents tab TRUNCATED chip.
 */
export interface ExtractedTextResult {
  /** Preview, capped at `EXTRACTED_TEXT_MAX_CHARS`. */
  text: string;
  /** Body, capped at `EXTRACTED_TEXT_HARD_MAX_CHARS`. */
  fullText: string;
  totalChars: number;
  truncated: boolean;
}

function capExtractedText(raw: string): ExtractedTextResult {
  return {
    text: raw.slice(0, PREVIEW_TEXT_LENGTH),
    fullText: raw.slice(0, MAX_TEXT_LENGTH),
    totalChars: raw.length,
    truncated: raw.length > MAX_TEXT_LENGTH,
  };
}

/** A `[…]` placeholder is not extracted content: zero chars, never truncated. */
function placeholderText(text: string): ExtractedTextResult {
  return { text, fullText: text, totalChars: 0, truncated: false };
}

/**
 * Office_Preview — true when a document is a PowerPoint file (.ppt / .pptx)
 * eligible for the server-side LibreOffice → PDF derived-preview pipeline.
 * Matches on either the file extension or the OOXML / legacy MS PowerPoint
 * mime so an upload with a missing/wrong mime is still recognised by ext.
 * Kept in sync with `classifyViewer`'s PowerPoint branch
 * (src/components/siterag/docViewers/index.tsx) and the convert endpoint's
 * extension allow-list (server.ts /api/documents/preview/convert).
 */
export function isOfficePreviewCandidate(fileName: string, mimeType?: string): boolean {
  const ext = fileName.split('.').pop()?.toLowerCase() ?? '';
  const mime = (mimeType ?? '').toLowerCase();
  return (
    ext === 'pptx' ||
    ext === 'ppt' ||
    mime.includes('presentationml') ||
    mime === 'application/vnd.ms-powerpoint'
  );
}

/** R-131-RAG-B — strip full body before site-entity embed (chunks live in subcollection). */
export function siteDocumentForEntityEmbed(doc: SiteDocument): SiteDocument {
  if (!doc.fullExtractedText) return doc;
  const { fullExtractedText: _full, ...rest } = doc;
  return rest;
}

/**
 * DLB-3 — tombstone TTL: a doc deleted within the last 30 days cannot
 * be silently re-ingested. Older tombstones are treated as stale and
 * cleaned up at the next ingest attempt. 30 days is the conservative
 * upper bound on "the user remembers deleting this on purpose";
 * shorter windows risk silently re-resurrecting docs the user
 * explicitly purged, longer windows clutter the admin Ghost Docs panel.
 */
export const TOMBSTONE_TTL_MS = 30 * 24 * 3600 * 1000;

/**
 * DLB-3 — thrown by `saveDocument` (and any future re-ingest entrypoint)
 * when the target docId has a fresh tombstone (< TOMBSTONE_TTL_MS old).
 * The caller is expected to either:
 *   - Surface a toast pointing at admin → Ghost Docs to purge the
 *     tombstone (the only sanctioned "undelete" path), or
 *   - Allocate a fresh docId for the new content (legitimate re-upload
 *     of a coincidentally same-hash file under a different identity).
 *
 * Carries diagnostic context so the admin panel + audit log can
 * surface "this re-ingest was blocked by an X-day-old tombstone".
 */
export class GhostReingestBlocked extends Error {
  readonly docId: string;
  readonly deletedAt: string;
  readonly reason: string;
  constructor(args: { docId: string; deletedAt: string; reason: string }) {
    super(
      `Document ${args.docId} was deleted on ${args.deletedAt} (reason: ${args.reason}). ` +
        `Re-ingest blocked. Purge the tombstone from admin → Ghost Docs to re-upload.`,
    );
    this.name = 'GhostReingestBlocked';
    this.docId = args.docId;
    this.deletedAt = args.deletedAt;
    this.reason = args.reason;
  }
}

/**
 * DLB-3 — check the user-scoped tombstone collection for a fresh
 * delete record. Returns:
 *   - `{ blocked: true, … }` if a fresh tombstone exists.
 *   - `{ blocked: false, staleCleared: true }` if a stale tombstone
 *     was discovered and cleared (caller can proceed).
 *   - `{ blocked: false, staleCleared: false }` if no tombstone exists.
 *
 * Never throws — failures (e.g. permission denied) degrade to
 * "no tombstone found" so the customer-visible upload path doesn't
 * regress on tombstone-read failures.
 */
export async function checkDocTombstone(
  docId: string,
): Promise<
  | { blocked: true; deletedAt: string; reason: string }
  | { blocked: false; staleCleared: boolean }
> {
  try {
    const { auth, db } = await import('../../firebase');
    const user = auth.currentUser;
    if (!user) return { blocked: false, staleCleared: false };
    const { doc, getDoc, deleteDoc } = await import('firebase/firestore');
    const tombRef = doc(db, `users/${user.uid}/deletedDocuments`, docId);
    const snap = await getDoc(tombRef);
    if (!snap.exists()) return { blocked: false, staleCleared: false };
    const data = snap.data() as {
      deletedAt?: { toMillis?: () => number } | string;
      reason?: string;
    };
    // Tombstones written client-side use serverTimestamp() → a
    // Firestore Timestamp with `toMillis()`. Tests / shimmed writes
    // may use ISO strings. Tolerate both.
    let deletedAtMs = 0;
    let deletedAtIso: string;
    if (typeof data.deletedAt === 'string') {
      deletedAtMs = Date.parse(data.deletedAt) || 0;
      deletedAtIso = data.deletedAt;
    } else if (data.deletedAt && typeof data.deletedAt.toMillis === 'function') {
      deletedAtMs = data.deletedAt.toMillis();
      deletedAtIso = new Date(deletedAtMs).toISOString();
    } else {
      // Malformed tombstone — treat as no-op (don't block, don't
      // clear). The admin panel surfaces it for manual triage.
      return { blocked: false, staleCleared: false };
    }
    const ageMs = Date.now() - deletedAtMs;
    if (ageMs >= 0 && ageMs < TOMBSTONE_TTL_MS) {
      return {
        blocked: true,
        deletedAt: deletedAtIso,
        reason: data.reason ?? 'unknown',
      };
    }
    // Stale tombstone — clear and let the caller proceed. Best-effort;
    // a failure here doesn't block the upload.
    try {
      await deleteDoc(tombRef);
    } catch (e) {
      console.warn(
        `[checkDocTombstone] failed to clear stale tombstone for ${docId}:`,
        (e as Error)?.message,
      );
    }
    return { blocked: false, staleCleared: true };
  } catch (e) {
    console.warn(
      `[checkDocTombstone] tombstone check threw for ${docId}:`,
      (e as Error)?.message,
    );
    return { blocked: false, staleCleared: false };
  }
}

/**
 * DLB-4 — denormalised tombstone record returned by `listDocTombstones`.
 * `deletedAt` is normalised to an ISO string for consumers (the wire
 * format is a Firestore Timestamp with `toMillis()`); the original
 * `Timestamp` is also surfaced when present so callers can compute
 * `daysToExpiry` with sub-millisecond precision against the
 * `TOMBSTONE_TTL_MS` constant.
 */
export interface DocTombstoneRecord {
  /** Same value as the Firestore document id — kept for ergonomic
   *  destructuring. */
  docId: string;
  /** Site the doc lived on at delete time. May be missing on legacy
   *  tombstones written before DLB-3 carried this field. */
  siteId?: string;
  /** ISO 8601 — normalised from the Firestore Timestamp. */
  deletedAt: string;
  /** Millisecond epoch — handy for client-side TTL maths so the panel
   *  doesn't have to re-parse the ISO. */
  deletedAtMs: number;
  /** Free-form classifier from the delete-call site. Current values:
   *  `'single-delete' | 'bulk-manual' | 'site-cascade' | 'sed-rebuild'`. */
  reason: string;
  /** Display name captured at delete-time so the panel can show the
   *  user a recognisable label without re-fetching the (by definition
   *  deleted) document. May be missing on legacy tombstones. */
  originalFileName?: string;
  /** Uid of the user who issued the delete. Always set for new
   *  tombstones — missing only on test fixtures / very old rows. */
  actorUid?: string;
  /** R-131-DR-C — GCS JSON path captured at delete time for version
   *  restore. Falls back to the canonical `users/{uid}/documents/{id}.json`
   *  when absent (legacy tombstones). */
  storagePath?: string;
  /** R-131-DR-C — raw upload bytes path when the doc had a download file. */
  rawStoragePath?: string;
}

/**
 * DLB-4 — read every tombstone for the active user. Returns a typed
 * array sorted by `deletedAt` descending so the admin Ghost Docs panel
 * shows the most-recent first.
 *
 * Returns an empty array (not undefined) on auth failures or read
 * errors so the panel can render a clean empty state.
 */
export async function listDocTombstones(): Promise<DocTombstoneRecord[]> {
  try {
    const { auth, db } = await import('../../firebase');
    const user = auth.currentUser;
    if (!user) return [];
    const { collection, getDocs } = await import('firebase/firestore');
    const snap = await getDocs(collection(db, `users/${user.uid}/deletedDocuments`));
    const out: DocTombstoneRecord[] = [];
    for (const d of snap.docs) {
      const data = d.data() as {
        docId?: string;
        siteId?: string;
        deletedAt?: { toMillis?: () => number; toDate?: () => Date } | string;
        reason?: string;
        originalFileName?: string;
        actorUid?: string;
        storagePath?: string;
        rawStoragePath?: string;
      };
      let deletedAtMs = 0;
      let deletedAtIso = '';
      if (typeof data.deletedAt === 'string') {
        deletedAtMs = Date.parse(data.deletedAt) || 0;
        deletedAtIso = data.deletedAt;
      } else if (data.deletedAt && typeof data.deletedAt.toMillis === 'function') {
        deletedAtMs = data.deletedAt.toMillis();
        deletedAtIso = new Date(deletedAtMs).toISOString();
      } else {
        // Malformed row — skip rather than render a bogus "0ms" entry.
        continue;
      }
      out.push({
        docId: data.docId ?? d.id,
        siteId: data.siteId,
        deletedAt: deletedAtIso,
        deletedAtMs,
        reason: data.reason ?? 'unknown',
        originalFileName: data.originalFileName,
        actorUid: data.actorUid,
        storagePath: data.storagePath,
        rawStoragePath: data.rawStoragePath,
      });
    }
    // Descending by deletedAt — newest first.
    out.sort((a, b) => b.deletedAtMs - a.deletedAtMs);
    return out;
  } catch (e) {
    console.warn('[listDocTombstones] read failed:', (e as Error)?.message);
    return [];
  }
}

/**
 * DLB-4 — delete a tombstone, returning the doc to its previous
 * re-uploadable state. The original document index row is NOT
 * recreated — purge is an admin override of the 30-day TTL, not a
 * true "undelete". The next attempted upload of `docId` will succeed
 * because `checkDocTombstone` will find no row.
 *
 * Returns `{ ok: true }` on success, `{ ok: false, error }` otherwise.
 * Never throws.
 */
export async function purgeDocTombstone(
  docId: string,
): Promise<{ ok: true } | { ok: false; error: string }> {
  try {
    const { auth, db } = await import('../../firebase');
    const user = auth.currentUser;
    if (!user) return { ok: false, error: 'Not signed in' };
    const { doc, deleteDoc } = await import('firebase/firestore');
    await deleteDoc(doc(db, `users/${user.uid}/deletedDocuments`, docId));
    return { ok: true };
  } catch (e) {
    return { ok: false, error: (e as Error)?.message ?? 'unknown error' };
  }
}

/**
 * Evidence_Setup_Auto_And_Modes ES3 — doc-upload completion rule for
 * `SiteEntity.evidenceMode`. Pure / synchronous so the store hook
 * (`maybeFlipEvidenceModeOnDocUpload`) and the tests can both call it.
 *
 *   - `undefined` / `'awaiting'` + ≥1 user doc  →  `'docs-included'`
 *     (the site has documents; "Set up site" is no longer the right label).
 *   - `'ai-only'`                               →  no change
 *     (the user explicitly opted out; an upload doesn't count + we never
 *      silently override that decision — they switch modes by hand).
 *   - `'docs-included'`                         →  no change.
 *   - any mode + zero user docs                 →  no change
 *     (deletions don't downgrade the mode; the user keeps whatever they
 *      last chose).
 *
 * Returns the *new* mode when a flip is warranted, else `null`.
 */
export function nextEvidenceModeAfterDocChange(
  current: SiteEntity['evidenceMode'],
  userDocCount: number,
): NonNullable<SiteEntity['evidenceMode']> | null {
  if (userDocCount <= 0) return null;
  if (current === undefined || current === 'awaiting') return 'docs-included';
  return null;
}

/**
 * Extract text content from an uploaded file.
 * Supports: text/plain, text/csv, application/pdf (basic), text/markdown.
 */
export async function extractTextFromFile(file: File, options: PdfExtractOptions = {}): Promise<string> {
  return (await extractTextFromFileDetailed(file, options)).text;
}

/**
 * Same dispatch as `extractTextFromFile`, but also reports how much text the
 * extractor produced before the `MAX_TEXT_LENGTH` cap so `processUploadedFile`
 * can stamp `extractedCharsTotal` / `truncated` on the SiteDocument.
 */
export async function extractTextFromFileDetailed(
  file: File,
  options: PdfExtractOptions = {},
): Promise<ExtractedTextResult> {
  const type = file.type || '';
  const name = file.name.toLowerCase();

  // Office lock files (`~$<name>.{docx,xlsx,pptx}`) are NOT real OOXML
  // archives — they're tiny hidden owner-record blobs Office creates
  // while a user has the real document open. JSZip / mammoth / SheetJS
  // all throw "Can't find end of central directory" on them. Detect by
  // the `~$` filename prefix and short-circuit to a labelled placeholder
  // so the corpus stays clean and the console stays quiet. The check is
  // before every format-specific branch so it covers DOCX / XLSX / PPTX
  // (and any future Office format) uniformly.
  const baseName = name.split('/').pop() ?? name;
  if (baseName.startsWith('~$')) {
    return placeholderText(`[Office lock file ${file.name} (${file.size} bytes) — skipped]`);
  }

  // Email (.eml / RFC 822, and .msg when it is actually RFC 822 text).
  // Parsed into identity headers + decoded body + attachment trailer. The
  // raw MIME source used to be stored verbatim, which spent the whole
  // MAX_TEXT_LENGTH budget on encoded signature images (2026-09-07).
  // Checked before the generic text branch because browsers report `.eml`
  // as `message/rfc822`, `text/plain` or an empty type depending on the OS.
  if (name.endsWith('.eml') || name.endsWith('.msg') || type === 'message/rfc822') {
    return extractEmailText(file);
  }

  // Plain text, CSV, markdown, JSON, HTML, XML, log, URL shortcut
  if (type.startsWith('text/') || name.endsWith('.md') || name.endsWith('.csv') || name.endsWith('.json')
    || name.endsWith('.txt') || name.endsWith('.log') || name.endsWith('.url') || name.endsWith('.xml')
    || name.endsWith('.html') || name.endsWith('.htm') || name.endsWith('.rtf')) {
    try {
      const text = await file.text();
      return capExtractedText(text);
    } catch {
      return placeholderText(`[Text extraction failed for ${file.name}]`);
    }
  }

  // PDF — extract text using the browser's built-in capabilities
  if (type === 'application/pdf' || name.endsWith('.pdf')) {
    const pdf = await extractPdfTextWithPages(file, options);
    return {
      text: pdf.fullText.slice(0, PREVIEW_TEXT_LENGTH),
      fullText: pdf.fullText,
      totalChars: pdf.totalChars,
      truncated: pdf.truncated,
    };
  }

  // DOCX — extract via mammoth (real unzip + XML parse). Document_Extraction_Hardening DE1.
  // The legacy byte-grep path (`extractDocxTextLegacyFallback`) is kept only as
  // a fall-through for the rare case mammoth returns nothing — DOCX with no
  // body text (forms-only). OOXML files are ZIPs and the inner XML is
  // DEFLATE-compressed, so a raw UTF-8 + `<w:t>` regex never matches.
  if (type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' || name.endsWith('.docx')) {
    return extractDocxTextViaMammoth(file);
  }

  // PPTX — extract slide-by-slide text via JSZip unzip + <a:t> grep.
  // Document_Extraction_Hardening DE3. The pre-DE3 path called the DOCX
  // byte-grep helper without unzipping the OOXML container, so the inner
  // (DEFLATE-compressed) XML was never visible to the regex.
  if (name.endsWith('.pptx') || name.endsWith('.ppt')) {
    return extractPptxText(file);
  }

  // XLSX — extract via SheetJS as per-sheet CSV. Document_Extraction_Hardening DE2.
  // The legacy fall-through to the DOCX byte-grep never worked (OOXML is
  // DEFLATE-compressed inside a ZIP) and also mislabelled the result as
  // "Word document" — fixed in DE4.
  if (name.endsWith('.xlsx') || name.endsWith('.xls')) {
    return extractXlsxText(file);
  }

  // SVG — vector image, no raster vision pass. Must precede the raster branch
  // because `image/svg+xml` matches `type.startsWith('image/')`.
  if (type === 'image/svg+xml' || name.endsWith('.svg')) {
    return placeholderText(`[Image (vector): ${file.name} (${(file.size / 1024).toFixed(0)} KB)]`);
  }

  // Images — multimodal vision pass via Gemini. Document_Extraction_Hardening DE5.
  // WEBP is added because Gemini 2.5 supports it.
  if (type.startsWith('image/') || name.endsWith('.jpg') || name.endsWith('.jpeg') || name.endsWith('.png')
    || name.endsWith('.gif') || name.endsWith('.bmp') || name.endsWith('.tiff') || name.endsWith('.webp')) {
    return extractImageDescription(file);
  }

  // KMZ/KML — geographic data
  if (name.endsWith('.kmz') || name.endsWith('.kml')) {
    return placeholderText(`[Geographic data: ${file.name} (${(file.size / 1024).toFixed(0)} KB)]`);
  }

  // Video — store metadata
  if (type.startsWith('video/') || name.endsWith('.mp4') || name.endsWith('.mov') || name.endsWith('.avi') || name.endsWith('.webm')) {
    return placeholderText(`[Video: ${file.name} (${(file.size / (1024 * 1024)).toFixed(1)} MB)]`);
  }

  // Fallback: try reading first 500KB as text (cap to avoid crash on large binary files)
  try {
    const slice = file.size > 500000 ? file.slice(0, 500000) : file;
    const text = await slice.text();
    // Only keep if it looks like actual text (>50% printable ASCII)
    const printable = text.split('').filter(c => c.charCodeAt(0) >= 32 && c.charCodeAt(0) < 127).length;
    if (printable / text.length < 0.5) return placeholderText(`[Binary file: ${file.name} (${(file.size / 1024).toFixed(0)} KB)]`);
    return capExtractedText(text);
  } catch {
    return placeholderText(`[Unable to extract text from ${file.name}]`);
  }
}

/**
 * `.eml` / `.msg` → identity headers + decoded body + attachment trailer.
 * RFC 822 text goes through `parseEmlToText`; a binary Outlook `.msg` (OLE2
 * container) goes through `parseMsgToEmail` (MS-OXMSG over the CFB reader
 * SheetJS ships). A compound file the reader cannot open gets an honest
 * placeholder. A mis-named plain text file that carries no email headers
 * keeps the pre-parser behaviour (stored as text).
 */
async function extractEmailText(file: File): Promise<ExtractedTextResult> {
  try {
    const bytes = new Uint8Array(await file.arrayBuffer());
    if (isOleCompoundFile(bytes)) {
      try {
        const { parseMsgToEmail } = await import('../../lib/msgParser');
        const parsed = await parseMsgToEmail(bytes);
        if (parsed.isEmail) return capExtractedText(parsed.text);
      } catch (e) {
        console.warn(`[msg] Outlook message parse failed for ${file.name}:`, (e as Error)?.message);
      }
      return placeholderText(
        `[Outlook message: ${file.name} (${(file.size / 1024).toFixed(0)} KB) — could not be read; `
        + 'save the email as .eml or PDF to make it readable]',
      );
    }
    const raw = new TextDecoder('utf-8', { fatal: false }).decode(bytes);
    const parsed = parseEmlToText(raw);
    if (!parsed.isEmail) return capExtractedText(raw);
    return capExtractedText(parsed.text);
  } catch {
    return placeholderText(`[Text extraction failed for ${file.name}]`);
  }
}

/**
 * PDF structural tokens that should NEVER appear in legitimately extracted
 * prose. Their presence means a decode failed and the raw PDF byte stream
 * (cross-reference streams, compressed object streams, Flate-wrapped object
 * bodies) leaked through as "text". PDF_Text_Extraction_Fix PE2/PE3.
 */
const PDF_STRUCTURAL_TOKENS = /\b(?:endstream|endobj|FlateDecode|ObjStm|CIDFontType|Identity-H)\b|\/(?:Filter|Length|Type)\s*[/\d<]|<<\/|>>\s*stream\b/;

/**
 * PDF_Text_Extraction_Fix PE2 — sanity guard on extracted text.
 *
 * Returns `true` only when `text` looks like real document prose, `false`
 * when it carries the fingerprints of a failed decode (PDF structural
 * tokens, an unhealthy ratio of control bytes, or runaway U+FFFD
 * replacement characters from a mis-applied codec). Bracketed placeholders
 * such as `[PDF: …]` are out of scope here — callers check those
 * separately; this function only judges purported *content*.
 */
export function isLikelyValidPdfText(text: string): boolean {
  if (text.length < 20) return false;
  // Bracketed placeholder lines ("[PDF: … scanned …]") aren't content —
  // don't run the content heuristics over them.
  if (/^\s*\[PDF:/.test(text)) return false;
  // PDF structural tokens never appear in extracted prose.
  if (PDF_STRUCTURAL_TOKENS.test(text)) return false;
  // High ratio of non-printable control bytes = binary noise leaking through.
  let nonPrintable = 0;
  for (let i = 0; i < text.length; i++) {
    const code = text.charCodeAt(i);
    if (code < 32 && code !== 9 && code !== 10 && code !== 13) nonPrintable++;
  }
  if (nonPrintable / text.length > 0.05) return false;
  // Excess U+FFFD replacement characters = the codec couldn't decode the bytes.
  const replacementCount = (text.match(/�/g) ?? []).length;
  if (replacementCount > text.length / 50) return false;
  return true;
}

/**
 * PDF_Text_Extraction_Fix PE3 — strict regex fallback for legacy / unusual
 * PDFs where pdfjs itself can't produce text.
 *
 * Pulls parenthesised operands (PDF `Tj` / `TJ` string literals) out of the
 * raw bytes, but rejects anything that smells of PDF structure or binary
 * noise: each kept segment must be 4–200 chars, contain no PDF structural
 * token, carry at least three word-shaped runs, and be entirely
 * ASCII-printable. The caller still applies `isLikelyValidPdfText` to the
 * joined result and requires a healthy segment count before returning it.
 */
function extractTextFromRegexFallback(raw: string): string[] {
  const segments: string[] = [];
  // 4+ chars, no nested or escaped parens.
  const regex = /\(([^()\\]{4,200})\)/g;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(raw)) !== null) {
    const seg = match[1].replace(/\\[nrt]/g, ' ').trim();
    if (seg.length < 4) continue;
    // Reject any PDF structural / font-machinery token.
    if (/(?:endstream|endobj|FlateDecode|ObjStm|Filter|stream|Adobe|Identity-H|CIDFont|XRef|Metadata|Catalog|Pages)/i.test(seg)) continue;
    // Require ≥3 word-shaped runs (4+ letters each).
    const wordCount = (seg.match(/[a-zA-Z]{4,}/g) ?? []).length;
    if (wordCount < 3) continue;
    // Reject if any character falls outside ASCII-printable.
    let allPrintable = true;
    for (let i = 0; i < seg.length; i++) {
      const code = seg.charCodeAt(i);
      if (code < 32 || code >= 127) { allPrintable = false; break; }
    }
    if (!allPrintable) continue;
    segments.push(seg);
  }
  return segments;
}

/**
 * PDF_Text_Extraction_Fix PE4 — detector for already-corrupted `textContent`.
 *
 * Returns `true` when a stored `textContent` string looks like it was
 * produced by the pre-fix extractor's regex fallback scraping the raw PDF
 * byte stream — i.e. it's binary noise / PDF structural tokens, not prose.
 * Used by the reprocess backfill (script + AdminPanel button) to decide
 * which docs need re-extracting. Bracketed `[PDF: …]` placeholders are
 * *not* flagged here (those are intentional — the extractor said "no text");
 * the backfill considers re-trying them separately on the chance the legacy
 * build can now decode the original.
 */
export function looksLikeCorruptedPdfText(text: string): boolean {
  if (!text) return false;
  // A clean placeholder line is not corruption — the backfill handles those
  // via a different branch.
  if (/^\s*\[PDF:/.test(text.trim())) return false;
  // Reuse the same content heuristics the extractor now applies on the way
  // out: structural tokens, control-byte noise, runaway U+FFFD.
  return !isLikelyValidPdfText(text);
}

/**
 * Document_Extraction_Hardening DE6 — true when the extracted text for a
 * PDF indicates pdfjs found zero text on every page (almost certainly a
 * scan / image-only PDF, where the fix path is OCR rather than another
 * text-extraction pass).
 *
 * Pattern-matches both placeholders emitted by `extractPdfTextFromBytes`:
 *   - `[PDF: <name> — no extractable text (may be scanned/image-based)]`
 *   - `[PDF: <name> (<KB>) — scanned or image-based, text extraction unavailable]`
 *
 * Only fires for PDFs — DOCX/XLSX/PPTX get their own extractors (DE1-DE3)
 * and their failure modes aren't OCR-recoverable. Returns `false` for any
 * non-PDF file and for clean PDF text.
 */
export function needsOcrForExtraction(file: { name: string; type?: string }, textContent: string): boolean {
  const name = file.name.toLowerCase();
  const isPdf = file.type === 'application/pdf' || name.endsWith('.pdf');
  if (!isPdf) return false;
  if (!textContent) return false;
  // The two scanned-PDF placeholder shapes emitted by extractPdfTextFromBytes.
  return (
    textContent.includes('— no extractable text (may be scanned/image-based)') ||
    textContent.includes('— scanned or image-based, text extraction unavailable')
  );
}

export type PdfOcrRunner = (base64PdfData: string, fileName: string) => Promise<string>;
export type PdfOcrBackend = 'vision-primary' | 'gemini-only';

/** SPO-1 — one rasterized PDF page, ready for a vision OCR call. */
export interface PdfPageImage {
  /** 1-based page number in the source PDF. */
  page: number;
  base64Data: string;
  mimeType: string;
}

/** SPO-1 — renders PDF pages to images. Returns `[]` when rasterization
 *  is unavailable (no canvas — Node backfills) so the caller can fall
 *  through to the whole-PDF OCR ladder. */
export type PdfPageRasterizer = (bytes: Uint8Array, maxPages: number, pageNumbers?: readonly number[]) => Promise<PdfPageImage[]>;

/** SPO-1 — transcribes ONE rasterized page. Returns `''` for an
 *  illegible/failed page (the caller keeps page offsets consistent). */
export type PdfPageOcrRunner = (image: PdfPageImage, label: string) => Promise<string>;

export interface PdfExtractOptions {
  /** Override OCR runner (tests/backfills). */
  pdfOcrRunner?: PdfOcrRunner;
  /** Keep an escape hatch for legacy/failure triage runs. */
  enableOcrFallback?: boolean;
  /** Explicit backend selection. Defaults to env-driven vision-primary. */
  ocrBackend?: PdfOcrBackend;
  /** SPO-1 — override the page rasterizer (tests / non-browser hosts). */
  pageRasterizer?: PdfPageRasterizer;
  /** SPO-1 — override the per-page vision OCR runner (tests). */
  pageOcrRunner?: PdfPageOcrRunner;
}

const MAX_PDF_OCR_BYTES = 15 * 1024 * 1024;

/** SPO-1 — page cap for the per-page vision OCR pass. Bounds Gemini
 *  cost on pathological scans; overflow is logged, never silent. */
export const MAX_PDF_OCR_PAGES = 25;

/** SPO-1 — raster cap. Rasterizing re-decodes the whole document; a
 *  runaway scan above this is left to the whole-PDF ladder (which has
 *  its own inline-payload cap). */
const MAX_PDF_RASTER_BYTES = 60 * 1024 * 1024;

/** SPO-1 — per-page OCR calls in flight at once. Small enough to stay
 *  polite to the proxy during batch uploads, large enough that a
 *  25-page scan doesn't serialize into minutes. */
const PAGE_OCR_CONCURRENCY = 3;

function base64FromBytes(bytes: Uint8Array): string {
  let binary = '';
  const CHUNK = 32 * 1024;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return typeof btoa === 'function'
    ? btoa(binary)
    : Buffer.from(binary, 'binary').toString('base64');
}

/** One page of extracted PDF text with its char span into `fullText`. */
export interface PdfExtractedPage {
  page: number;
  charStart: number;
  charEnd: number;
  text: string;
}

/**
 * PDF extraction result. `fullText` is capped at `MAX_TEXT_LENGTH` (with
 * `pages[]` clamped to it); `totalChars` is the pre-cap length (0 for
 * placeholders) and `truncated` flags a cut. Additive fields — script
 * callers that only read `fullText` / `pages` are unaffected.
 */
export interface PdfExtractResult {
  fullText: string;
  pages: PdfExtractedPage[];
  totalChars: number;
  truncated: boolean;
  /** Physical pages with little or no text, retained even when other pages have text. */
  visualPageNumbers?: number[];
  pdfCoverage?: { totalPages: number; textPagesRead: number };
}

async function tryExtractPdfTextViaOcr(
  bytes: Uint8Array,
  label: string,
  options: PdfExtractOptions = {},
): Promise<PdfExtractResult | null> {
  if (options.enableOcrFallback === false) return null;
  if (bytes.length > MAX_PDF_OCR_BYTES) {
    console.info(`[pdf-ocr] skipped for ${label}: file ${Math.round(bytes.length / (1024 * 1024))}MB exceeds ${MAX_PDF_OCR_BYTES / (1024 * 1024)}MB inline cap`);
    return null;
  }
  try {
    const runner = options.pdfOcrRunner ?? (async (base64PdfData: string, fileName: string) => {
      const backend: PdfOcrBackend =
        options.ocrBackend
        ?? (await import('../geminiClient')).DOC_OCR_BACKEND;
      const { extractPdfTextViaVisionApi, extractPdfTextForSite } = await import('../geminiService');
      if (backend === 'vision-primary') {
        const visionText = await extractPdfTextViaVisionApi(base64PdfData, fileName);
        if (visionText.length >= 50) return visionText;
      }
      return extractPdfTextForSite(base64PdfData, fileName);
    });
    const text = (await runner(base64FromBytes(bytes), label)).trim();
    if (text.length < 50) return null;
    const fullText = text.slice(0, MAX_TEXT_LENGTH);
    return {
      fullText,
      pages: [{ page: 1, charStart: 0, charEnd: fullText.length, text: fullText }],
      totalChars: text.length,
      truncated: text.length > MAX_TEXT_LENGTH,
    };
  } catch (e) {
    console.warn(`[pdf-ocr] fallback failed for ${label}:`, (e as Error)?.message);
    return null;
  }
}

/**
 * SPO-1 — default page rasterizer: pdfjs render → canvas → JPEG.
 *
 * Browser-only in practice (needs a 2D canvas); returns `[]` anywhere
 * a canvas context can't be created (Node backfills, jsdom tests) so
 * the caller falls through to the whole-PDF OCR ladder. Renders at up
 * to 2.5× so small scan text stays legible to the vision model while
 * JPEG q0.85 keeps each page payload in the low hundreds of KB — the
 * same payload shape as the live-proven image vision path.
 */
export async function rasterizePdfPagesToImages(
  bytes: Uint8Array,
  maxPages: number,
  pageNumbers?: readonly number[],
): Promise<PdfPageImage[]> {
  if (typeof document === 'undefined' || typeof document.createElement !== 'function') return [];
  if (bytes.length > MAX_PDF_RASTER_BYTES) {
    console.info(`[pdf-ocr] page rasterization skipped: file ${Math.round(bytes.length / (1024 * 1024))}MB exceeds ${MAX_PDF_RASTER_BYTES / (1024 * 1024)}MB raster cap`);
    return [];
  }
  try {
    const [pdfjsLib] = await Promise.all([
      import('pdfjs-dist/legacy/build/pdf.mjs'),
      import('pdfjs-dist/legacy/build/pdf.worker.mjs'),
    ]);
    const pdf = await pdfjsLib.getDocument({ data: bytes }).promise;
    const selectedPages = pageNumbers
      ? [...new Set(pageNumbers)].filter(p => Number.isInteger(p) && p > 0 && p <= pdf.numPages).slice(0, maxPages)
      : Array.from({ length: Math.min(pdf.numPages, maxPages) }, (_, i) => i + 1);
    if (pdf.numPages > maxPages) {
      console.info(`[pdf-ocr] ${pdf.numPages}-page document capped at ${maxPages} pages for per-page OCR`);
    }
    const images: PdfPageImage[] = [];
    for (const i of selectedPages) {
      try {
        const page = await pdf.getPage(i);
        const base = page.getViewport({ scale: 1 });
        // Target ~1800px on the long edge — legible scan text without
        // ballooning the per-page payload.
        const scale = Math.min(2.5, Math.max(1, 1800 / Math.max(base.width, base.height)));
        const viewport = page.getViewport({ scale });
        const canvas = document.createElement('canvas');
        canvas.width = Math.ceil(viewport.width);
        canvas.height = Math.ceil(viewport.height);
        const ctx = canvas.getContext('2d');
        if (!ctx) return [];
        await page.render({ canvas, canvasContext: ctx, viewport }).promise;
        const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
        const comma = dataUrl.indexOf(',');
        if (comma < 0) continue;
        images.push({ page: i, base64Data: dataUrl.slice(comma + 1), mimeType: 'image/jpeg' });
      } catch (e) {
        console.warn(`[pdf-ocr] rasterizing page ${i} failed:`, (e as Error)?.message);
      }
    }
    return images;
  } catch (e) {
    console.warn('[pdf-ocr] page rasterization unavailable:', (e as Error)?.message);
    return [];
  }
}

/**
 * SPO-1 — per-page vision OCR for scanned/image-based PDFs.
 *
 * Root cause of the live dead-end (six Redhead docs stuck at
 * `needsOcr: true` + `[PDF: …]` placeholder): the whole-PDF OCR
 * fallbacks ship the ENTIRE document as one inline payload, which is
 * exactly the shape that dies on multi-MB scans (proxy body limits,
 * inline-data caps) — while single-image vision calls work live. This
 * path rasterizes each page and transcribes it through the same
 * one-image-per-call shape, then reassembles `fullText` + `pages[]`
 * with real per-page char offsets (same join/offset/truncation
 * convention as the pdfjs text path).
 *
 * Page failures degrade per-page (empty text, page skipped in the
 * assembly); the pass succeeds when the total yield clears the same
 * 50-char gate the whole-PDF ladder uses. Returns `null` when
 * rasterization is unavailable or the yield is insufficient.
 */
async function tryExtractPdfTextViaPageVisionOcr(
  bytes: Uint8Array,
  label: string,
  options: PdfExtractOptions = {},
): Promise<PdfExtractResult | null> {
  if (options.enableOcrFallback === false) return null;
  try {
    const rasterize = options.pageRasterizer ?? rasterizePdfPagesToImages;
    const images = await rasterize(bytes, MAX_PDF_OCR_PAGES);
    if (images.length === 0) return null;

    const runner: PdfPageOcrRunner = options.pageOcrRunner ?? (async (image, fileLabel) => {
      const { transcribePdfPageImage } = await import('../geminiService');
      return transcribePdfPageImage(image.base64Data, image.mimeType, fileLabel, image.page);
    });

    // Bounded-concurrency transcription; result order pinned by index.
    const texts: string[] = new Array(images.length).fill('');
    let next = 0;
    const worker = async () => {
      while (next < images.length) {
        const idx = next++;
        try {
          texts[idx] = (await runner(images[idx]!, label)).trim();
        } catch (e) {
          console.warn(`[pdf-ocr] page ${images[idx]!.page} OCR failed for ${label}:`, (e as Error)?.message);
        }
      }
    };
    await Promise.all(Array.from({ length: Math.min(PAGE_OCR_CONCURRENCY, images.length) }, worker));

    // Assemble non-empty pages with the same sequential-numbering +
    // char-offset convention as the pdfjs text path.
    const pageTexts = texts
      .map((text, index) => ({ text, page: images[index]!.page }))
      .filter((entry) => entry.text.length > 0);
    const totalChars = pageTexts.reduce((n, entry) => n + entry.text.length, 0);
    if (totalChars < 50) return null;

    let fullText = '';
    const pages: PdfExtractedPage[] = [];
    for (let i = 0; i < pageTexts.length; i++) {
      if (i > 0) fullText += '\n\n';
      const charStart = fullText.length;
      fullText += pageTexts[i]!.text;
      pages.push({ page: pageTexts[i]!.page, charStart, charEnd: fullText.length, text: '' });
    }
    const producedChars = fullText.length;
    fullText = fullText.slice(0, MAX_TEXT_LENGTH);
    for (const p of pages) {
      p.charEnd = Math.min(p.charEnd, fullText.length);
      p.charStart = Math.min(p.charStart, fullText.length);
      p.text = fullText.slice(p.charStart, p.charEnd);
    }
    console.info(`[pdf-ocr] per-page vision OCR recovered ${fullText.length} chars from ${pageTexts.length}/${images.length} pages of ${label}`);
    return { fullText, pages, totalChars: producedChars, truncated: producedChars > MAX_TEXT_LENGTH };
  } catch (e) {
    console.warn(`[pdf-ocr] per-page vision OCR failed for ${label}:`, (e as Error)?.message);
    return null;
  }
}

/**
 * SPO-1 — OCR fallback ladder for scanned/image-based PDFs:
 *   1. per-page rasterized vision OCR (browser; proven payload shape),
 *   2. whole-PDF OCR (server Vision FILES endpoint → Gemini inline PDF;
 *      also the Node-backfill path, where rasterization returns []).
 */
async function tryExtractPdfOcrLadder(
  bytes: Uint8Array,
  label: string,
  options: PdfExtractOptions = {},
): Promise<PdfExtractResult | null> {
  return (
    (await tryExtractPdfTextViaPageVisionOcr(bytes, label, options))
    ?? (await tryExtractPdfTextViaOcr(bytes, label, options))
  );
}

export async function extractPdfWithPagesFromBytes(
  bytes: Uint8Array,
  label: string,
  options: PdfExtractOptions = {},
): Promise<PdfExtractResult> {
  const empty: PdfExtractResult = {
    fullText: `[PDF: ${label} — no extractable text (may be scanned/image-based)]`,
    pages: [{ page: 1, charStart: 0, charEnd: 0, text: '' }],
    totalChars: 0,
    truncated: false,
  };
  try {
    const [pdfjsLib] = await Promise.all([
      import('pdfjs-dist/legacy/build/pdf.mjs'),
      import('pdfjs-dist/legacy/build/pdf.worker.mjs'),
    ]);
    const pdf = await pdfjsLib.getDocument({ data: bytes }).promise;
    const pageTexts: Array<{ page: number; text: string }> = [];
    const visualPageNumbers: number[] = [];
    const maxPages = Math.min(pdf.numPages, PDF_TEXT_PAGE_CAP);
    const pdfCoverage = { totalPages: pdf.numPages, textPagesRead: maxPages };
    for (let i = 1; i <= maxPages; i++) {
      const page = await pdf.getPage(i);
      const content = await page.getTextContent();
      const pageText = content.items
        .map((item) => ('str' in item ? item.str : ''))
        .join(' ')
        .trim();
      if (isPdfVisualCandidate(pageText, label, pdf.numPages)) visualPageNumbers.push(i);
      if (pageText) pageTexts.push({ page: i, text: pageText });
    }
    if (pageTexts.length > 0) {
      const joined = pageTexts.map((entry) => entry.text).join('\n\n');
      if (isLikelyValidPdfText(joined)) {
        let fullText = '';
        const pages: PdfExtractedPage[] = [];
        for (let i = 0; i < pageTexts.length; i++) {
          const { page, text } = pageTexts[i]!;
          if (i > 0) fullText += '\n\n';
          const charStart = fullText.length;
          fullText += text;
          pages.push({ page, charStart, charEnd: fullText.length, text: '' });
        }
        const producedChars = fullText.length;
        fullText = fullText.slice(0, MAX_TEXT_LENGTH);
        for (const p of pages) {
          p.charEnd = Math.min(p.charEnd, fullText.length);
          p.text = fullText.slice(p.charStart, p.charEnd);
        }
        return { fullText, pages, totalChars: producedChars, truncated: producedChars > MAX_TEXT_LENGTH || pdf.numPages > maxPages, pdfCoverage, ...(visualPageNumbers.length ? { visualPageNumbers } : {}) };
      }
      console.warn(`[pdf] extraction returned non-text content for ${label} — treating as scanned/image-based`);
      const ocr = await tryExtractPdfOcrLadder(bytes, label, options);
      return { ...(ocr ?? empty), pdfCoverage, truncated: (ocr?.truncated ?? false) || pdf.numPages > maxPages, ...(visualPageNumbers.length ? { visualPageNumbers } : {}) };
    }
    const ocr = await tryExtractPdfOcrLadder(bytes, label, options);
    return { ...(ocr ?? empty), pdfCoverage, truncated: (ocr?.truncated ?? false) || pdf.numPages > maxPages, ...(visualPageNumbers.length ? { visualPageNumbers } : {}) };
  } catch (e) {
    console.warn(`[pdf] pdfjs extraction failed for ${label}, trying regex fallback:`, (e as Error)?.message);
    try {
      const slice = bytes.length > 1024 * 1024 ? bytes.subarray(0, 1024 * 1024) : bytes;
      const raw = new TextDecoder('utf-8', { fatal: false }).decode(slice);
      const segments = extractTextFromRegexFallback(raw);
      if (segments.length > 10) {
        const joined = segments.join(' ');
        if (isLikelyValidPdfText(joined)) {
          const fullText = joined.slice(0, MAX_TEXT_LENGTH);
          return {
            fullText,
            pages: [{ page: 1, charStart: 0, charEnd: fullText.length, text: fullText }],
            totalChars: joined.length,
            truncated: joined.length > MAX_TEXT_LENGTH,
          };
        }
      }
    } catch { /* exhausted fallbacks */ }
    const ocr = await tryExtractPdfOcrLadder(bytes, label, options);
    if (ocr) return ocr;
    const kb = (bytes.length / 1024).toFixed(0);
    return {
      fullText: `[PDF: ${label} (${kb} KB) — scanned or image-based, text extraction unavailable]`,
      pages: [{ page: 1, charStart: 0, charEnd: 0, text: '' }],
      totalChars: 0,
      truncated: false,
    };
  }
}

/**
 * Core PDF text extraction over raw bytes — shared by the browser upload
 * path (`extractPdfText`) and the Node reprocess backfill
 * (`scripts/reprocessPdfText.ts`).
 *
 * PDF_Text_Extraction_Fix PE1 — uses the `pdfjs-dist/legacy` build, which
 * inlines the FlateDecode + compressed-object-stream (`/Type/ObjStm`) +
 * CIDFont decoders that the modern build only ships in its web worker. With
 * the worker disabled (`workerSrc = ''`, our long-standing main-thread setup)
 * the modern build silently fails on every PDF 1.5+ document — cross-ref
 * streams and compressed object streams have been the norm for years — and
 * the regex fallback then scraped the raw byte stream and returned PDF
 * structural tokens (`endstream`, `FlateDecode`, `ObjStm`, …) as "text". The
 * legacy build keeps the main-thread architecture and gains the decoders.
 *
 * PE2/PE3 — the primary output is sanity-checked (`isLikelyValidPdfText`)
 * before it's returned; if it fails, or if pdfjs throws, we fall back to a
 * strict parenthesised-operand scrape (`extractTextFromRegexFallback`) that
 * is itself re-validated. When nothing passes, a bracketed `[PDF: …]`
 * placeholder is returned — never binary noise.
 */
export async function extractPdfTextFromBytes(
  bytes: Uint8Array,
  label: string,
  options: PdfExtractOptions = {},
): Promise<string> {
  // Preview semantics on purpose: the string callers (reprocess backfills,
  // supply-chain flows) write `textContent`. The whole body is available
  // from `extractPdfWithPagesFromBytes().fullText`.
  const { fullText } = await extractPdfWithPagesFromBytes(bytes, label, options);
  return fullText.slice(0, PREVIEW_TEXT_LENGTH);
}

async function extractPdfTextWithPages(
  file: File,
  options: PdfExtractOptions = {},
): Promise<PdfExtractResult> {
  const buffer = await file.arrayBuffer();
  return extractPdfWithPagesFromBytes(new Uint8Array(buffer), file.name, options);
}

/**
 * Document_Extraction_Hardening DE1 — primary DOCX extractor.
 *
 * Uses `mammoth.extractRawText({ arrayBuffer })` which actually unzips the
 * OOXML container and parses the inner XML — the only correct way to read
 * a `.docx`. The pre-DE1 path read raw bytes as UTF-8 and regex-greppe'd for
 * `<w:t>` tags, which never matched because the inner XML is DEFLATE-compressed
 * inside the ZIP — every DOCX upload returned the `[Word document: …]`
 * placeholder instead of its body text.
 *
 * Falls through to `extractDocxTextLegacyFallback` only when mammoth returns
 * (almost) nothing — usually a DOCX with no body text (forms-only). The
 * fallback then either scrapes any leaked text-runs or returns an honest
 * placeholder labelled by extension (DE4).
 */
async function extractDocxTextViaMammoth(file: File): Promise<ExtractedTextResult> {
  try {
    const buffer = await file.arrayBuffer();
    const mammoth = await import('mammoth');
    // mammoth's `openZip` looks at `arrayBuffer` in the browser build and at
    // `buffer` in the Node build (a Uint8Array works for both code paths).
    // Pass both keys so the same call works under Vite (browser entry) and
    // under vitest/node (main entry).
    const { value } = await mammoth.extractRawText({
      arrayBuffer: buffer,
      buffer: new Uint8Array(buffer),
    } as Parameters<typeof mammoth.extractRawText>[0]);
    const cleaned = value.replace(/\s+\n/g, '\n').replace(/\n{3,}/g, '\n\n').trim();
    if (cleaned.length < 30) {
      // Mammoth returned (almost) nothing — likely an unusual DOCX layout
      // (forms-only, no body text). Fall through to the legacy regex path
      // as a best-effort, then to the placeholder.
      return extractDocxTextLegacyFallback(file);
    }
    return capExtractedText(cleaned);
  } catch (e) {
    console.warn(`[docx] mammoth extraction failed for ${file.name}:`, (e as Error)?.message);
    return extractDocxTextLegacyFallback(file);
  }
}

/**
 * Document_Extraction_Hardening DE2 — XLSX/XLS extractor via SheetJS.
 *
 * Per-sheet cell-address projection — a compact, source-anchored shape for
 * project matrices, cost models, and schedules. SheetJS unzips the OOXML
 * workbook and emits non-empty cells with their original sheet, row and cell
 * addresses. Formatted empty columns/rows never expand into comma-only text.
 * Dates and cached formula values are preserved; formulas without a cached
 * value stay explicitly unevaluated. Merged cells retain their stored anchor.
 *
 * The pre-DE2 path fell through to the DOCX byte-grep extractor, which
 * never matched (OOXML is DEFLATE-compressed inside a ZIP) and returned a
 * `[Word document: …xlsx …]` placeholder — both wrong-extractor and
 * wrong-label.
 */
async function extractXlsxText(file: File): Promise<ExtractedTextResult> {
  try {
    const buffer = await file.arrayBuffer();
    const XLSX = await import('xlsx');
    const wb = XLSX.read(buffer, { type: 'array', cellDates: true, cellNF: false, cellText: true });
    const sheets: string[] = [];
    for (const sheetName of wb.SheetNames) {
      const ws = wb.Sheets[sheetName];
      if (!ws) continue;
      const { worksheetEvidenceText } = await import('../../lib/spreadsheetText');
      const trimmed = worksheetEvidenceText(ws, sheetName);
      if (trimmed.length === 0) continue;
      sheets.push(trimmed);
    }
    if (sheets.length === 0) {
      return placeholderText(`[Spreadsheet: ${file.name} (${(file.size / 1024).toFixed(0)} KB) — empty workbook (no rows)]`);
    }
    return capExtractedText(sheets.join('\n\n'));
  } catch (e) {
    console.warn(`[xlsx] extraction failed for ${file.name}:`, (e as Error)?.message);
    return placeholderText(`[Spreadsheet: ${file.name} (${(file.size / 1024).toFixed(0)} KB) — text extraction failed]`);
  }
}

/**
 * Document_Extraction_Hardening DE3 — PPTX extractor via JSZip + `<a:t>` grep.
 *
 * Properly unzips the OOXML container and walks every slide XML
 * (`ppt/slides/slide{N}.xml`), pulling text-runs out of `<a:t>` tags. The
 * pre-DE3 path called the DOCX byte-grep helper without unzipping, so the
 * inner DEFLATE-compressed XML was never visible to the regex.
 *
 * Mammoth doesn't support PPTX (Word-only), so we fall back to JSZip + a
 * narrow regex. JSZip is already in deps (also used by `kmzParser.ts`).
 *
 * Slide order is preserved by sorting on the numeric suffix in
 * `slide{N}.xml`. Each slide gets a `### Slide N` header so the LLM can
 * cite by slide number when summarising.
 */
async function extractPptxText(file: File): Promise<ExtractedTextResult> {
  try {
    const JSZip = (await import('jszip')).default;
    const buffer = await file.arrayBuffer();
    const zip = await JSZip.loadAsync(buffer);
    const slideTexts: string[] = [];
    // PPTX slides live at ppt/slides/slide{N}.xml; sort to keep order
    const slideEntries = Object.keys(zip.files)
      .filter(k => /^ppt\/slides\/slide\d+\.xml$/.test(k))
      .sort((a, b) => {
        const an = parseInt(a.match(/slide(\d+)/)?.[1] ?? '0', 10);
        const bn = parseInt(b.match(/slide(\d+)/)?.[1] ?? '0', 10);
        return an - bn;
      });
    for (const key of slideEntries) {
      const entry = zip.files[key];
      if (!entry) continue;
      const xml = await entry.async('string');
      const runs: string[] = [];
      const re = /<a:t[^>]*>([^<]*)<\/a:t>/g;
      let m: RegExpExecArray | null;
      while ((m = re.exec(xml)) !== null) {
        const v = m[1].trim();
        if (v.length > 0) runs.push(v);
      }
      if (runs.length > 0) {
        const slideNum = parseInt(key.match(/slide(\d+)/)?.[1] ?? '0', 10);
        slideTexts.push(`### Slide ${slideNum}\n${runs.join(' ')}`);
      }
    }
    if (slideTexts.length === 0) {
      return placeholderText(`[PowerPoint: ${file.name} (${(file.size / 1024).toFixed(0)} KB) — no slide text (image-only deck?)]`);
    }
    return capExtractedText(slideTexts.join('\n\n'));
  } catch (e) {
    console.warn(`[pptx] extraction failed for ${file.name}:`, (e as Error)?.message);
    return placeholderText(`[PowerPoint: ${file.name} (${(file.size / 1024).toFixed(0)} KB) — text extraction failed]`);
  }
}

/**
 * Document_Extraction_Hardening DE5 — describe an uploaded image via Gemini
 * multimodal vision.
 *
 * Pre-DE5 every image upload (JPG/PNG/etc.) returned `[Image: <name> (<KB>)]`
 * — the AI saw the filename and nothing else. Site photos can carry visible
 * evidence (terrain, structures, signage, plat markings, neighbouring land
 * use) that's directly relevant to feasibility, and Gemini 2.5 already
 * supports inline image data; this wires the bytes through a vision call
 * and stores the resulting description as `textContent`.
 *
 * Output shape: `[Image vision: <fileName>]\n\n<3–6 paragraph description>`.
 * The `[Image vision: …]` prefix lets the LLM (and the renderer)
 * distinguish a vision-derived description from real prose extracted from
 * an Office document. When the vision call returns nothing usable, falls
 * back to the metadata-only placeholder so downstream code sees a
 * predictable shape either way.
 *
 * Size cap: 10 MB — Gemini's inline-image limit varies by model; 10 MB is
 * the safe boundary across the 2.5 family. Larger files skip the call and
 * fall back to the metadata-only placeholder.
 */
async function extractImageDescription(file: File): Promise<ExtractedTextResult> {
  const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
  if (file.size > MAX_IMAGE_BYTES) {
    return placeholderText(`[Image: ${file.name} (${(file.size / 1024).toFixed(0)} KB) — too large for vision pass; metadata only]`);
  }
  try {
    const { describeImageForSite } = await import('../geminiService');
    const buffer = await file.arrayBuffer();
    // Base64-encode the bytes for Gemini's `inlineData`. Walk in 32 KB chunks
    // — `String.fromCharCode(...new Uint8Array(buffer))` blows the V8 argument
    // limit on anything over ~125 KB.
    const bytes = new Uint8Array(buffer);
    let binary = '';
    const CHUNK = 32 * 1024;
    for (let i = 0; i < bytes.length; i += CHUNK) {
      binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
    }
    const b64 = typeof btoa === 'function'
      ? btoa(binary)
      : Buffer.from(binary, 'binary').toString('base64');
    const mime = file.type || 'image/jpeg';
    const description = await describeImageForSite(b64, mime, file.name);
    if (description && description.length > 30) {
      return capExtractedText(`[Image vision: ${file.name}]\n\n${description}`);
    }
    return placeholderText(`[Image: ${file.name} (${(file.size / 1024).toFixed(0)} KB) — vision pass returned no description]`);
  } catch (e) {
    console.warn(`[image] vision extraction failed for ${file.name}:`, (e as Error)?.message);
    return placeholderText(`[Image: ${file.name} (${(file.size / 1024).toFixed(0)} KB)]`);
  }
}

/**
 * Legacy DOCX/PPTX extractor — byte-greppy fallback.
 *
 * Reads the OOXML container as a UTF-8 string and regex-greps `<w:t>` /
 * `<a:t>` tags. Only succeeds when the inner XML happens not to be
 * DEFLATE-compressed (rare in practice — almost every Office-produced .docx
 * is compressed). Kept as a defence-in-depth fallback behind
 * `extractDocxTextViaMammoth` and the legacy PPTX/XLSX fall-throughs.
 *
 * Caps buffer read to 2MB to avoid crashing on large presentation files.
 */
async function extractDocxTextLegacyFallback(file: File): Promise<ExtractedTextResult> {
  try {
    // Cap at 2MB to avoid memory issues with large PPTX/DOCX files
    const maxBytes = 2 * 1024 * 1024;
    const slice = file.size > maxBytes ? file.slice(0, maxBytes) : file;
    const buffer = await slice.arrayBuffer();
    const text = new TextDecoder('utf-8', { fatal: false }).decode(buffer);

    // Extract only XML text content between <w:t> and <a:t> tags (Word/PowerPoint text runs)
    const xmlTextRuns: string[] = [];
    const tagRegex = /<(?:w:t|a:t)[^>]*>([^<]+)<\/(?:w:t|a:t)>/g;
    let match;
    while ((match = tagRegex.exec(text)) !== null) {
      const content = match[1].trim();
      if (content.length > 0) xmlTextRuns.push(content);
    }

    if (xmlTextRuns.length > 0) {
      return capExtractedText(xmlTextRuns.join(' '));
    }

    // Fallback: strip all XML tags and filter to readable words only
    const stripped = text.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
    const words = stripped.split(' ').filter(w => w.length > 2 && /^[a-zA-Z0-9]/.test(w) && !/[^\x20-\x7E]/.test(w));
    const readable = words.join(' ');

    // If less than 30% of the output is readable ASCII, it's binary garbage.
    // Document_Extraction_Hardening DE4 — pick the placeholder label from the
    // file extension so an XLSX that lands here (via a pre-DE2 wiring or a
    // future fall-through) is labelled "Spreadsheet", never "Word document".
    if (readable.length < 100 || readable.length / stripped.length < 0.3) {
      return placeholderText(`[${placeholderLabelForFile(file.name)}: ${file.name} (${(file.size / 1024).toFixed(0)} KB) — binary content, text extraction limited]`);
    }

    return capExtractedText(readable);
  } catch {
    return placeholderText(`[${placeholderLabelForFile(file.name)} extraction failed: ${file.name}]`);
  }
}

/**
 * Document_Extraction_Hardening DE4 — pick an honest placeholder label for
 * a file based on its extension. The pre-DE4 legacy fallback hard-coded the
 * label as `Word document` for anything other than .pptx/.ppt, which meant
 * an XLSX that fell through (e.g. the pre-DE2 wiring) was labelled "Word
 * document: …xlsx" — wrong-type and confusing in the Documents tab.
 *
 * Centralised so the extractor branches all share the same vocabulary.
 */
function placeholderLabelForFile(fileName: string): string {
  const ext = fileName.toLowerCase().split('.').pop() ?? '';
  if (ext === 'xlsx' || ext === 'xls') return 'Spreadsheet';
  if (ext === 'pptx' || ext === 'ppt') return 'PowerPoint';
  if (ext === 'docx' || ext === 'doc') return 'Word document';
  return 'Document';
}

/**
 * Compute SHA-256 hex digest of a File's raw bytes. Used by the
 * cross-site doc reuse detector (Documents_On_Site Prompt 7-C). Best-effort:
 * returns undefined when crypto.subtle is unavailable (test environments)
 * or the file size would balloon memory.
 */
export async function computeFileHash(file: File): Promise<string | undefined> {
  if (typeof crypto === 'undefined' || !crypto.subtle) return undefined;
  // Bound at RAW_UPLOAD_MAX_BYTES (70 MB) so every file within the raw upload
  // limit gets a SHA-256 hash for deduplication and checksum verification.
  if (file.size > 70 * 1024 * 1024) return undefined;
  try {
    const buf = await file.arrayBuffer();
    const hash = await crypto.subtle.digest('SHA-256', buf);
    return Array.from(new Uint8Array(hash))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
  } catch (e) {
    console.warn(`[Doc] hash failed for ${file.name}:`, (e as Error)?.message);
    return undefined;
  }
}

/**
 * URE-2 — opts threaded through `processUploadedFile` so the upload
 * pipeline knows which Coverage Matrix requirement(s) the user
 * intended this doc to fulfill. Drives:
 *
 *   - `SiteDocument.intendedRequirementIds` — persisted on the doc.
 *   - `classifyDocumentRelevance` — gets a hint nudging Gemini toward
 *     the intended requirement's category tags.
 *   - `extractFromDocument` — consults `REQUIREMENT_TO_SECTION_MAP` to
 *     prioritise the intended requirement's SED sections first.
 *
 * All fields are optional; an unprovided opts argument reproduces
 * the pre-URE behaviour exactly (back-compat).
 */
export interface ProcessUploadedFileOpts {
  /** Optional stable docId to reuse across retries/reloads so retries never duplicate records. */
  docId?: string;
  /** Optional caller AbortSignal for cancellation. */
  signal?: AbortSignal;
  /** Optional stage progress callback. */
  onStageChange?: (stage: 'extracting' | 'summarizing' | 'indexing' | 'classifying') => void | Promise<void>;
  summaryRunner?: (text: string, fileName: string) => Promise<{ summary: string; tags: string[] }>;
  /** Server bulk ingestion has no browser auth context for eager DM2 scoring. */
  skipEagerScoring?: boolean;
  /** Shared browser/admin PDF extraction adapters. */
  pdfOptions?: PdfExtractOptions;
  /** Visual reading runner injection and optional extraction-only backfill switch. */
  visualRunner?: import('./analyzeImageEvidence').ImageGeminiRunner;
  skipVisualReading?: boolean;
  /** Explicit bulk-ingestion budget; browser default is 25, maximum is the 400-page text ceiling. */
  visualPageLimit?: number;
  /** Requirement ids the user clicked UPLOAD for (e.g. `['utility-letter']`). */
  intendedRequirementIds?: string[];
  /** Provenance of the intent — drives audit trails. CMU-2 adds
   *  `coverage-matrix-replace-button` for the populated-row replace
   *  flow; same downstream wiring as `coverage-matrix-upload-button`
   *  but distinguishable in audit reports. */
  intentSource?:
    | 'coverage-matrix-upload-button'
    | 'coverage-matrix-replace-button'
    | 'free-drop'
    | 'bulk-import'
    | 'rerun-attach';
  /** Optional user-id stamp for the intent (audit). */
  intentSetBy?: string;
  /**
   * DMS-FOLLOWUP-AUTO-SCORE-ON-UPLOAD — siteId for the eager DM2
   * scorer's audit metadata + per-site rebuild-cache keying. Optional
   * because legacy callers (FeasibilityRerunPanel, store-level
   * uploads) don't know the active site directly; when omitted, the
   * eager scorer still fires but the audit row carries `siteId: null`.
   */
  siteId?: string;
  /**
   * ES3 / DAE3 — when true, skip eager DM2 scoring (docs stored but
   * excluded from evidence pipeline in ai-only mode).
   */
  skipEvidencePipeline?: boolean;
  /** P3 — test hook: model call for the content index. */
  topicSegmentRunner?: import('./documentTopics').TopicSegmentRunner;
  /** P3 — skip the content index (bulk backfills that only need text). */
  skipTopicIndex?: boolean;
}

/**
 * Process an uploaded file: extract text, summarize with Gemini, build SiteDocument.
 *
 * URE-2 — `opts` carries upload-time intent (the user clicked the
 * Coverage Matrix UPLOAD button for a specific requirement). When
 * supplied, the resulting `SiteDocument` carries `intendedRequirementIds`
 * + `intentSetAt`/`intentSetBy` audit stamps so downstream consumers
 * (Coverage Matrix slot routing, doc-aware fallback prioritisation,
 * `DOC_OVERRIDE_RULES` secondary trigger) can use the explicit intent
 * signal alongside the existing classifier-derived tag.
 */
/** Map labels can exceed the sparse-text threshold while most evidence remains visual. */
export function isPdfVisualCandidate(text: string, fileName: string, totalPages: number): boolean {
  const trimmed = text.trim();
  if (trimmed.length < 100) return true;
  if (trimmed.length > 2000) return false;
  return /\b(?:legend|pipelines?|flood\s+zone|site\s+boundary|site\s+diagram|(?:location|soil)\s+map|lidar\s+imagery|scale\s*1\s*:)\b/i.test(trimmed)
    || (totalPages <= 3 && /\b(?:map|diagram|plat)\b/i.test(fileName));
}

export async function processUploadedFile(
  file: File,
  opts: ProcessUploadedFileOpts = {},
): Promise<SiteDocument> {
  if (opts.signal?.aborted) {
    throw new DOMException('The operation was aborted.', 'AbortError');
  }
  await opts.onStageChange?.('extracting');
  const deferredStages = new Set<'summarization' | 'topic-indexing' | 'classification'>();
  // P1.2 — `textContent` is the prompt PREVIEW (`PREVIEW_TEXT_LENGTH`);
  // `fullExtractedText` is the whole body up to the ceiling. Pages carry the
  // full text in memory; the site-record projection trims them on persist.
  let textContent = '';
  let fullExtractedText = '';
  let pages: SiteDocument['pages'];
  let visualPageNumbers: number[] | undefined;
  let pdfCoverage: SiteDocument['pdfCoverage'];
  // Extraction-cap visibility — what the extractor produced, how much was
  // kept, and whether the ceiling cut anything (TRUNCATED chip).
  let extractedCharsTotal: number | undefined;
  let truncated = false;
  try {
    const lowerName = file.name.toLowerCase();
    const isPdf = file.type === 'application/pdf' || lowerName.endsWith('.pdf');
    if (isPdf) {
      const pdfResult = await extractPdfTextWithPages(file, opts.pdfOptions);
      fullExtractedText = pdfResult.fullText;
      textContent = fullExtractedText.slice(0, PREVIEW_TEXT_LENGTH);
      pages = pdfResult.pages;
      visualPageNumbers = pdfResult.visualPageNumbers;
      pdfCoverage = pdfResult.pdfCoverage;
      extractedCharsTotal = pdfResult.totalChars;
      truncated = pdfResult.truncated;
    } else {
      const extracted = await extractTextFromFileDetailed(file, opts.pdfOptions);
      fullExtractedText = extracted.fullText;
      textContent = extracted.text;
      extractedCharsTotal = extracted.totalChars;
      truncated = extracted.truncated;
      pages = [{ page: 1, charStart: 0, charEnd: fullExtractedText.length, text: fullExtractedText }];
    }
  } catch (e) {
    console.warn(`[Doc] Text extraction failed for ${file.name}:`, e);
    textContent = `[Extraction failed: ${file.name}]`;
    fullExtractedText = textContent;
  }

  // Read visual evidence before classification so map labels enter the same
  // content routing, RAG and SED extraction surfaces as document text.
  let visualReadings: SiteDocument['visualReadings'];
  let visualCoverage: SiteDocument['visualCoverage'];
  let visualIia: import('./imageExtractors').IiaPipelineResult | undefined;
  const testRunner = typeof process !== 'undefined' && !!process.env?.VITEST;
  if (!opts.skipVisualReading && !opts.skipEvidencePipeline && (!testRunner || opts.visualRunner)) {
    try {
      const { readDocumentVisual, visualReadingText } = await import('./documentVisualReading');
      const visualBlobs: { blob: Blob; page?: number }[] = [];
      if (file.type.startsWith('image/') && !file.type.includes('svg')) visualBlobs.push({ blob: file, page: 1 });
      else if (file.type === 'application/pdf' || /\.pdf$/i.test(file.name)) {
        const physicalPageCount = pdfCoverage?.totalPages ?? pages?.length ?? 0;
        const sparsePageNumbers = visualPageNumbers ?? pages?.filter(p => isPdfVisualCandidate(p.text, file.name, physicalPageCount)).map(p => p.page);
        // Mixed PDFs may have map-only pages despite a healthy total text yield.
        if ((extractedCharsTotal ?? 0) < 100 || (sparsePageNumbers?.length ?? 0) > 0) {
          const raster = opts.pdfOptions?.pageRasterizer ?? rasterizePdfPagesToImages;
          const visualLimit = Number.isFinite(opts.visualPageLimit)
            ? Math.max(1, Math.min(PDF_TEXT_PAGE_CAP, Math.floor(opts.visualPageLimit!))) : MAX_PDF_OCR_PAGES;
          const images = await raster(new Uint8Array(await file.arrayBuffer()), visualLimit, sparsePageNumbers);
          const candidates = sparsePageNumbers ?? images.map(i => i.page);
          visualCoverage = { candidatePages: candidates, attemptedPages: images.map(i => i.page), unreadPages: [...candidates], limit: visualLimit };
          for (const img of images) {
            if ((extractedCharsTotal ?? 0) >= 100 && !sparsePageNumbers?.includes(img.page)) continue;
            const bytes = Uint8Array.from(atob(img.base64Data), c => c.charCodeAt(0));
            visualBlobs.push({ blob: new Blob([bytes], { type: img.mimeType }), page: img.page });
          }
          // Whole-PDF reader also works in admin hosts without a canvas.
          if (images.length === 0 && file.size <= MAX_PDF_OCR_BYTES) visualBlobs.push({ blob: file });
        }
      }
      for (const entry of visualBlobs) {
        const result = await readDocumentVisual(entry.blob, {
          page: entry.page, runner: opts.visualRunner,
          // PDF pages already enter section extraction as source-quoted text.
          // Running standalone-image classification on every scanned page adds
          // two calls whose typed result is not persisted for PDFs.
          skipIia: file.type === 'application/pdf' || /\.pdf$/i.test(file.name),
        });
        if (!visualIia && result.iia) visualIia = result.iia;
        if (result.reading) (visualReadings ??= []).push(result.reading);
      }
      if (visualCoverage) visualCoverage.unreadPages = visualCoverage.candidatePages.filter(page => !visualReadings?.some(r => r.page === page));
      if (visualReadings?.length) {
        const original = (extractedCharsTotal ?? 0) > 0 ? fullExtractedText : '';
        const readingText = visualReadingText(visualReadings);
        // Offsets still refer to the original document text; the vision block
        // is explicitly labelled and each observation carries its source page.
        const combined = [original, readingText].filter(Boolean).join('\n\n');
        const capped = capExtractedText(combined);
        fullExtractedText = capped.fullText;
        textContent = capped.text;
        extractedCharsTotal = capped.totalChars;
        truncated ||= capped.truncated;
        const visualPages = visualReadings.filter(r => typeof r.page === 'number').map(r => {
          const text = visualReadingText([r]);
          const charStart = fullExtractedText.indexOf(text);
          const start = Math.max(0, charStart);
          const end = Math.min(fullExtractedText.length, start + text.length);
          return { page: r.page!, charStart: start, charEnd: end, text: fullExtractedText.slice(start, end) };
        });
        pages = [...(original ? pages ?? [] : []), ...visualPages];
      }
    } catch (error) {
      console.warn(`[Doc] visual reading deferred for ${file.name}:`, (error as Error).message);
    }
  }

  // Compute content hash for cross-site reuse detection (Prompt 7-C).
  // Run in parallel with the summarize call below — the hash is cheap.
  const contentHashPromise = computeFileHash(file);

  if (opts.signal?.aborted) {
    throw new DOMException('The operation was aborted.', 'AbortError');
  }
  await opts.onStageChange?.('summarizing');

  // Summarize with timeout — large batches can overwhelm the API.
  let summary = '';
  let tags: string[] = [];
  const lowerName = file.name.toLowerCase();
  const isLargeFormat = lowerName.endsWith('.pptx') || lowerName.endsWith('.xlsx');
  const timeoutMs = isLargeFormat ? 30000 : 15000;
  const summarizeController = new AbortController();
  const onParentSummarizeAbort = () => summarizeController.abort(opts.signal?.reason);
  if (opts.signal) {
    opts.signal.addEventListener('abort', onParentSummarizeAbort, { once: true });
  }
  let summarizeTimer: ReturnType<typeof setTimeout> | undefined;
  try {
    const result = await Promise.race([
      opts.summaryRunner
        ? opts.summaryRunner(textContent, file.name)
        : summarizeDocument(textContent, file.name, undefined, {
            signal: summarizeController.signal,
            throwOnAbortOrTimeout: true,
            timeoutMs,
          }),
      new Promise<{ summary: string; tags: string[] }>((_, reject) => {
        summarizeTimer = setTimeout(() => {
          summarizeController.abort(new Error('timeout'));
          reject(new Error('timeout'));
        }, timeoutMs);
      }),
    ]);
    summary = result.summary;
    tags = result.tags;
  } catch (e) {
    if (opts.signal?.aborted) {
      throw new DOMException('The operation was aborted.', 'AbortError');
    }
    deferredStages.add('summarization');
    console.warn(`[Doc] summarize fallback for ${file.name}:`, (e as Error)?.message);
    const { deriveSummaryFromFilename, deriveTagsFromFilename } = await (await import('../../utils/safeImport')).safeImport(() => import('./driveConnector'));
    tags = deriveTagsFromFilename(file.name);
    // SET-D3 — prefer the first sentence of the extracted text over
    // the filename. Only kick in when extraction actually produced
    // useful content (≥50 chars and not the [Extraction failed: …]
    // sentinel above).
    const trimmedText = textContent?.trim() ?? '';
    const looksExtracted =
      trimmedText.length >= 50 &&
      !trimmedText.startsWith('[Extraction failed:');
    if (looksExtracted) {
      const sentenceMatch = trimmedText.split(/(?<=[.!?])\s/)[0];
      const firstSentence = (sentenceMatch && sentenceMatch.length > 0)
        ? sentenceMatch
        : trimmedText.slice(0, 200);
      const truncated = firstSentence.length > 300;
      const head = truncated ? firstSentence.slice(0, 300) + '…' : firstSentence;
      const kchars = (trimmedText.length / 1000).toFixed(1);
      summary = `${head}\n\n[Auto-summary unavailable — showing first sentence of extracted text. ${kchars}k chars total extracted.]`;
    } else {
      summary = deriveSummaryFromFilename(file.name);
    }
  } finally {
    if (summarizeTimer) clearTimeout(summarizeTimer);
    if (opts.signal) opts.signal.removeEventListener('abort', onParentSummarizeAbort);
  }

  const contentHash = await contentHashPromise;
  // URE-2 — normalise the intended-requirement opts. Empty array
  // collapses to `undefined` so legacy / free-drop docs don't carry
  // an empty marker that would skew the Tier-2 sufficiency walk.
  const cleanIntents = (opts.intendedRequirementIds ?? []).filter(Boolean);
  // SPO-1 — a scanned PDF whose whole OCR ladder (per-page vision +
  // whole-PDF) still produced nothing carries only a `[PDF: …]`
  // placeholder. Stamping `'pending'` here used to let the next forced
  // scan resolve it to a misleading `'success-full'`; the honest
  // terminal state is `'failed-no-text'`.
  const scanNeedsOcr = needsOcrForExtraction(file, textContent);
  const siteDoc: SiteDocument = {
    id: opts.docId || `doc-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    fileName: file.name,
    mimeType: file.type || 'application/octet-stream',
    sizeBytes: file.size,
    summary,
    tags,
    textContent,
    fullExtractedText,
    ...(visualReadings?.length ? { visualReadings, visualReadingCount: visualReadings.length } : {}),
    ...(visualCoverage ? { visualCoverage } : {}),
    ...(pdfCoverage ? { pdfCoverage } : {}),
    ...(pages && pages.length > 0 ? { pages } : {}),
    // Extraction-cap visibility (see above). `truncated` is only ever
    // stamped `true` — an absent field means nothing was cut.
    ...(extractedCharsTotal !== undefined
      ? { extractedCharsTotal, extractedCharsKept: fullExtractedText.length }
      : {}),
    ...(truncated ? { truncated: true } : {}),
    uploadedAt: new Date().toISOString(),
    ...(contentHash ? { contentHash } : {}),
    // Document_Extraction_Hardening DE6 — flag scanned/image-only PDFs so the
    // admin "Documents needing OCR" view can list unresolved cases. The upload
    // path now attempts the SPO-1 OCR ladder (per-page vision OCR, then
    // whole-PDF Vision/Gemini) for scanned PDFs; this flag remains set only
    // when the whole ladder failed or returned insufficient text.
    ...(scanNeedsOcr ? { needsOcr: true } : {}),
    // URE-1 — upload-time intent. Only stamped when the caller
    // explicitly threaded a non-empty `intendedRequirementIds`
    // (typically from the Coverage Matrix UPLOAD pre-tag).
    ...(cleanIntents.length > 0 ? {
      intendedRequirementIds: cleanIntents,
      intentSetAt: Date.now(),
      ...(opts.intentSetBy ? { intentSetBy: opts.intentSetBy } : {}),
    } : {}),
    // UPE-1 — every new doc starts as `'pending'`; the next SED
    // rebuild's `applyDocAwareConfidence` pass flips this to a
    // success-* / failed-* terminal status.
    //
    // SPO-1 exception — placeholder-only scanned PDFs start at the
    // honest terminal `'failed-no-text'` instead: there is nothing a
    // scan could extract, and the pending→forced-scan path used to
    // launder exactly these docs into `'success-full'`.
    extractionStatus: scanNeedsOcr ? 'failed-no-text' : 'pending',
    ...(scanNeedsOcr
      ? { extractionStatusError: NO_MACHINE_READABLE_TEXT_MESSAGE, extractionStatusAt: Date.now() }
      : {}),
  };

  // P3 (document-intelligence plan) — content index over the WHOLE body:
  // topical segments with offsets, pages, the SED sections and requirements
  // each speaks to, and stated attribution. Routing below reads it. Best-
  // effort; a skipped or failed pass leaves the filename/summary routing.
  let topicSummaries: string[] = [];
  // Under vitest the default runner would reach for the model client; only
  // index when a test supplies its own runner.
  const inTestRunner = typeof process !== 'undefined' && !!process.env?.VITEST;
  const topicIndexEnabled = !opts.skipTopicIndex && !opts.skipEvidencePipeline
    && (opts.topicSegmentRunner !== undefined || !inTestRunner);
  if (topicIndexEnabled) {
    if (opts.signal?.aborted) {
      throw new DOMException('The operation was aborted.', 'AbortError');
    }
    await opts.onStageChange?.('indexing');
    try {
      const { segmentDocumentTopics, TOPIC_MIN_BODY_CHARS } = await import('./documentTopics');
      const indexed = await segmentDocumentTopics(siteDoc, {
        runner: opts.topicSegmentRunner,
        abortSignal: opts.signal,
        timeoutMs: 45_000,
      });
      if (indexed) {
        siteDoc.topicSegments = indexed.segments;
        siteDoc.topics = indexed.topics;
        topicSummaries = indexed.segments.map(s => `${s.topic}: ${s.summary}`);
        if (indexed.topics.coverage?.windows.some(w => w.status === 'failed')) {
          deferredStages.add('topic-indexing');
        }
      } else if ((fullExtractedText?.length ?? 0) >= TOPIC_MIN_BODY_CHARS && !scanNeedsOcr) {
        deferredStages.add('topic-indexing');
      }
    } catch (e) {
      if (opts.signal?.aborted) {
        throw new DOMException('The operation was aborted.', 'AbortError');
      }
      deferredStages.add('topic-indexing');
      console.warn(`[Doc] content index failed for ${file.name} (routing falls back to summary/filename):`, (e as Error)?.message);
    }
  }

  // Documents_On_Site Prompt 2 — score the doc against every active report
  // type + the SED catalogue. Best-effort: a classifier failure (Gemini
  // outage, malformed response) falls back to keyword heuristics inside
  // classifyDocumentRelevance, so siteDoc still gets relevance scores
  // even when the network's misbehaving. Never block the upload on this.
  await opts.onStageChange?.('classifying');
  try {
    const relevance = await classifyDocumentRelevance({
      summary: siteDoc.summary,
      tags: siteDoc.tags,
      fileName: siteDoc.fileName,
      // P3 — content, not just the filename and summary.
      bodyExcerpt: fullExtractedText.slice(0, 6_000),
      topicSummaries,
      reportTypes: Object.keys(REPORT_TYPE_REGISTRY) as ReportTypeId[],
      sedSections: SED_SECTION_IDS,
      // URE-2 — explicit user intent for the classifier prompt.
      ...(cleanIntents.length > 0 ? { requirementHints: cleanIntents } : {}),
    });
    siteDoc.relevance = relevance.byReportType;
    // P3 — the sections the content index found join the classifier's.
    siteDoc.sedSectionsHelpful = Array.from(new Set([
      ...relevance.sedSections,
      ...(siteDoc.topics?.sedSections ?? []),
    ]));
    siteDoc.relevanceUpdatedAt = new Date().toISOString();
    siteDoc.relevanceModelVersion = CLASSIFIER_VERSION;
  } catch (e) {
    deferredStages.add('classification');
    console.warn(`[Doc] relevance classifier failed for ${file.name} — admin reclassify will fill this in:`, (e as Error)?.message);
  }

  // Doc_Classification_And_Evidence_Labels AC1 — content-based auto-tagging.
  try {
    const tagged = await classifyAndMergeDocTags(siteDoc);
    if (tagged.autoTaggedAt) {
      siteDoc.tags = tagged.tags;
      siteDoc.autoTaggedAt = tagged.autoTaggedAt;
      siteDoc.autoTagModelVersion = tagged.autoTagModelVersion;
    } else {
      console.info(`[Doc] content auto-tagging skipped for ${file.name}: no usable extracted text`);
    }
  } catch (e) {
    deferredStages.add('classification');
    console.warn(`[Doc] content auto-tagging failed for ${file.name} — admin backfill will fill this in:`, (e as Error)?.message);
  }
  siteDoc.enrichmentStatus = deferredStages.size > 0 ? 'deferred' : 'complete';
  if (deferredStages.size > 0) {
    siteDoc.enrichmentDeferredStages = Array.from(deferredStages);
  }

  // IIA-4 — Image Intelligence Analyzer pipeline. Only fires for image
  // MIME uploads. Classifies → extracts (per-kind structured) → falls
  // back to free-text observation. Result is stamped on
  // `siteDoc.iiaResult` so:
  //   (a) the Coverage Matrix resolver (`pickStrongestUserDoc` →
  //       `deriveExtractionModality`) can stamp `extractionModality:
  //       'vision'` on the envelope and the CAF-4 chip flips to the
  //       `📷 Image (vision)` variant;
  //   (b) the SED bootstrap can pull `iiaResult.observation` into
  //       `siteContext.claims[]` when the structured path failed.
  //
  // Defensive — failures in the IIA pipeline NEVER block the upload;
  // the siteDoc still saves without an iiaResult so the user can
  // re-trigger via the admin "Re-run image analysis" button later.
  if (siteDoc.mimeType.startsWith('image/') && !siteDoc.mimeType.includes('svg')) {
    try {
      const { dispatchImageExtraction, requirementIdForImageKind } = await import('./imageExtractors');
      const iia = visualIia ?? await dispatchImageExtraction(file, { runner: opts.visualRunner });
      // Stamp the targetRequirementIds onto the doc's intent — even
      // when extraction failed, the kind hint tells the Coverage
      // Matrix where the doc most likely belongs.
      const targetReq = iia.targetRequirementId
        ?? requirementIdForImageKind(iia.classification.kind);
      if (targetReq && (!siteDoc.intendedRequirementIds || siteDoc.intendedRequirementIds.length === 0)) {
        siteDoc.intendedRequirementIds = [targetReq];
        siteDoc.intentSetAt = Date.now();
        siteDoc.intentSetBy = 'iia-vision';
      }
      // Build the persisted iiaResult — match the SiteDocument.iiaResult
      // shape. `extracted` carries the validated zod data when present;
      // observation is the IIA-3 free-text fallback.
      siteDoc.iiaResult = {
        classification: iia.classification,
        ...(iia.extraction && iia.extraction.kind !== 'extraction-failure'
          ? { extracted: iia.extraction.data as Record<string, unknown> }
          : {}),
        ...(iia.observation
          ? {
              observation: {
                observationText: iia.observation.observationText,
                ...(iia.observation.relevantSections
                  ? { relevantSections: iia.observation.relevantSections }
                  : {}),
                userApproved: iia.observation.userApproved,
              },
            }
          : {}),
        ...(targetReq ? { targetRequirementIds: [targetReq] } : {}),
        runAt: iia.runAt,
        attemptResult: iia.attemptResult,
      };
    } catch (e) {
      // IIA pipeline is best-effort. Log + move on — siteDoc.iiaResult
      // stays undefined and the doc just looks like a regular image
      // upload to the Coverage Matrix (modality defaults to 'text').
      console.warn(`[Doc] IIA pipeline failed for ${file.name}:`, (e as Error)?.message);
    }
  }

  // Generation & Impact screening evidence — relevant future uploads are
  // extracted before persistence. Gemini identifies quoted raw facts only;
  // deterministic validation in the dedicated service owns units, basis,
  // applicability, hash binding, and eligibility. Evidence-disabled uploads
  // deliberately skip the call and remain unavailable to automatic prefill.
  if (!opts.skipEvidencePipeline) {
    try {
      const {
        extractGenerationScreeningEvidence,
        isGenerationScreeningDocumentRelevant,
      } = await import('./generationScreeningExtraction');
      if (isGenerationScreeningDocumentRelevant(siteDoc)) {
        siteDoc.generationScreeningExtraction =
          await extractGenerationScreeningEvidence(siteDoc);
      }
    } catch (e) {
      // Upload persistence must remain available during an extraction outage.
      // The Generation loader / SED document pass can idempotently backfill
      // this missing version using the document content hash.
      console.warn(
        `[Doc] generation screening extraction deferred for ${file.name}:`,
        (e as Error)?.message,
      );
    }
  }

  // DMS-FOLLOWUP-AUTO-SCORE-ON-UPLOAD — eager DM2 score on upload so the
  // DocumentLibrary's doc-match chip is correct on first paint instead of
  // showing "no match" until the lazy SED-rebuild pass fills the cache.
  // Fire-and-forget by contract:
  //   - never awaited (uploads must not block on Gemini latency);
  //   - any thrown error is swallowed inside `eagerScoreDocOnUpload`;
  //   - the .catch() below is a belt-and-braces guard that catches the
  //     theoretically-impossible reject before the unhandledrejection
  //     channel sees it.
  // The helper internally cost-gates (top-K=5 by tag overlap, 50/hour
  // per-user budget) so the upload-time fan-out stays bounded.
  // ES3 — skipped in ai-only mode (doc stored but inactive).
  if (!opts.skipEvidencePipeline && !opts.skipEagerScoring) {
    void (async () => {
      try {
        const { eagerScoreDocOnUpload } = await import('../docMatching/eagerScoreOnUpload');
        await eagerScoreDocOnUpload(opts.siteId ?? '', siteDoc);
      } catch (e) {
        console.debug('[Doc] eager DM2 score swallowed:', (e as Error)?.message);
      }
    })();
  }

  const { buildDocumentEvidenceDigest } = await import('../../lib/sed/documentEvidenceDigest');
  siteDoc.evidenceDigest = buildDocumentEvidenceDigest(siteDoc);
  return siteDoc;
}

/**
 * SCM — detect BOM schedules on upload when the site plan is empty.
 * Does NOT persist — callers must route through the review dialog.
 */
export async function maybeExtractBomFromUploadedDocument(
  document: SiteDocument,
): Promise<
  | { status: 'skipped'; reason: 'no-text' | 'not-bom' | 'no-lines' }
  | { status: 'bom-candidate'; classification: import('../supplyChain/classifyBomDocument').BomDocumentClassification; extraction: import('../supplyChain/extractBomFromPdf').BomExtractionResult }
> {
  const text = document.textContent?.trim() ?? '';
  if (!text) return { status: 'skipped', reason: 'no-text' };
  const { classifyBomDocument, shouldExtractBomFromDocument } = await import('../supplyChain/classifyBomDocument');
  const classification = classifyBomDocument(text, { fileName: document.fileName });
  if (!shouldExtractBomFromDocument(classification)) {
    return { status: 'skipped', reason: 'not-bom' };
  }
  const { extractBomFromPdf } = await import('../supplyChain/extractBomFromPdf');
  const extraction = await extractBomFromPdf(text);
  if (extraction.lines.length === 0) return { status: 'skipped', reason: 'no-lines' };
  return { status: 'bom-candidate', classification, extraction };
}

/**
 * SCM12 — upload-completion hook for inbound RFQ-response routing.
 *
 * Called by the upload-page caller (DocumentLibrary, FeasibilityRerunPanel)
 * after `processUploadedFile` + `saveDocument` resolved successfully, but
 * ONLY when the active site carries a non-empty `supplyChainPlan` — there's
 * nothing to attach a quote to otherwise. Returns the SCM12 pipeline result
 * the caller can use to:
 *   - persist the updated `plan` back onto the site;
 *   - persist the updated `document` (now carrying `linkedBomItemIds`);
 *   - surface the per-pipeline notification on the Quotes tab / toast.
 *
 * Wrapped in a try/catch so a classifier or extractor failure NEVER blocks
 * the regular document upload path — the file is already saved, the SCM12
 * pipeline is a value-add on top. On error we return a `'rejected'`-style
 * verdict carrying the failure message so the UI can show a non-blocking
 * "auto-extraction skipped" indicator.
 *
 * Dynamic-import the SCM12 module so the bundle's main upload path stays
 * lean — the SCM12 helpers are pulled in on first quote-response.
 */
export async function maybeProcessUploadedFileAsRfqResponse(args: {
  document: SiteDocument;
  supplyChainPlan: import('../../types/supplyChain').SupplyChainPlan | undefined;
  options?: import('../supplyChain/processRfqResponse').ProcessRfqResponseOptions;
}): Promise<
  | import('../supplyChain/processRfqResponse').ProcessRfqResponseResult
  | { status: 'skipped'; reason: 'no-plan' | 'empty-plan' | 'error'; error?: string }
  | { status: 'bom-candidate'; classification: import('../supplyChain/classifyBomDocument').BomDocumentClassification; extraction: import('../supplyChain/extractBomFromPdf').BomExtractionResult }
> {
  const { document, supplyChainPlan, options } = args;
  if (!supplyChainPlan) return { status: 'skipped', reason: 'no-plan' };
  if (supplyChainPlan.bom.length === 0 && supplyChainPlan.quotes.length === 0) {
    try {
      const bomCandidate = await maybeExtractBomFromUploadedDocument(document);
      if (bomCandidate.status === 'bom-candidate') {
        return bomCandidate;
      }
    } catch (e) {
      console.warn('[SCM] BOM upload detection failed:', (e as Error)?.message ?? e);
    }
    return { status: 'skipped', reason: 'empty-plan' };
  }
  try {
    const { processRfqResponse } = await import('../supplyChain/processRfqResponse');
    return await processRfqResponse(document, supplyChainPlan, options);
  } catch (e) {
    const err = e instanceof Error ? e.message : String(e);
    console.warn(`[SCM12] RFQ-response auto-routing failed for ${document.fileName}: ${err}`);
    return { status: 'skipped', reason: 'error', error: err };
  }
}

/**
 * True hard cap on the raw file we will attempt to persist. Files larger
 * than this surface a clean `too-large` warning and skip the upload
 * entirely (no round-trip). 70 MB is the product limit for original-bytes
 * preview/download — see the chunked upload path below for HOW files up to
 * this size actually reach GCS.
 *
 * NOTE (PDF-large-file fix, 2026-06): the previous comment claimed the
 * effective limit was ~74.6 MB via the single-shot `/api/storage/upload-file`
 * base64-in-JSON POST. That was WRONG in production: a base64 JSON body is
 * subject to **Cloud Run's ~32 MiB request limit**, which Cloud Run enforces
 * at its front-end proxy *before* express ever sees the body — so the
 * express `100mb` limit was irrelevant and the real effective ceiling on
 * this path was only ~23 MB of raw bytes (32 MiB / 1.37 base64 inflation,
 * minus JSON framing). A 33 MB PDF therefore 413'd with "raw file failed to
 * save" despite being well under this 70 MB guard. Files above
 * {@link CHUNKED_UPLOAD_THRESHOLD_BYTES} now upload via the chunked,
 * server-side resumable path (`/api/storage/upload-file-chunked`), which has
 * no 32 MiB ceiling on the server→GCS leg.
 */
const RAW_UPLOAD_MAX_BYTES = 70 * 1024 * 1024;

/**
 * PDF-large-file fix — raw-byte threshold above which a file is uploaded via
 * the chunked endpoint instead of the single-shot
 * `/api/storage/upload-file`. Kept conservative so the base64-inflated body
 * of a SINGLE chunk stays well under Cloud Run's ~32 MiB request limit:
 *   20 MB raw → ~26.7 MB base64 → ~27 MB JSON body ≪ 32 MiB.
 * Small files (logos, typical docs) stay on the proven single-shot path —
 * this threshold is the only behavioural fork. Each chunk is sized at this
 * same value (the last chunk is the remainder).
 */
const CHUNKED_UPLOAD_THRESHOLD_BYTES = 20 * 1024 * 1024;

/**
 * PDF-large-file fix — per-chunk raw-byte size for the chunked upload. Equal
 * to the threshold: a file just over the threshold splits into two chunks
 * (e.g. 33 MB → 20 MB + 13 MB). Each chunk's base64 body stays comfortably
 * under Cloud Run's 32 MiB request limit.
 */
const RAW_UPLOAD_CHUNK_BYTES = 20 * 1024 * 1024;

/**
 * PDF-large-file fix — base64-encode a Blob slice via FileReader. Mirrors the
 * single-shot path's FileReader usage so the test FileReader shim keeps
 * working; returns just the base64 payload (strips the
 * `data:...;base64,` prefix). Reading a Blob slice (not the whole File)
 * keeps peak memory to one chunk at a time.
 */
async function blobToBase64(blob: Blob): Promise<string> {
  const reader = new FileReader();
  return new Promise<string>((resolve, reject) => {
    reader.onload = () => resolve((reader.result as string).split(',')[1] ?? '');
    reader.onerror = () => reject(new Error('Failed to read file chunk'));
    reader.readAsDataURL(blob);
  });
}

/**
 * PDF-large-file fix — upload a large raw file to `rawPath` in sequential
 * chunks via `/api/storage/upload-file-chunked`. The server assembles the
 * chunks into the GCS object through a resumable write stream (no 32 MiB
 * ceiling on the server→GCS leg), so files between ~23 MB and
 * {@link RAW_UPLOAD_MAX_BYTES} that would 413 on the single-shot path now
 * succeed.
 *
 * Protocol (one `uploadId` per file, all phases authed + JSON):
 *   1. `{ phase: 'init', path, contentType, totalBytes, totalChunks }`
 *      → server opens a resumable GCS write stream, returns `{ uploadId }`.
 *   2. `{ phase: 'chunk', uploadId, index, content }` for each chunk in
 *      order (`content` = base64 of the slice). Server appends to the stream.
 *   3. `{ phase: 'finalize', uploadId }` → server ends the stream, the GCS
 *      object is complete at `path`.
 *
 * Each phase carries the shared `signal` so the existing per-attempt
 * AbortController timeout still bounds the whole sequence. On any failure,
 * a best-effort `{ phase: 'abort', uploadId }` is sent so the server can
 * tear down the partial stream; the error then propagates to the caller's
 * non-fatal `rawUploadWarning` handler.
 */
export interface UploadRawFileChunkedOptions {
  /** Optional deterministic/resumable session ID so retries resume without re-sending chunks. */
  uploadId?: string;
  /** Expected SHA-256 hex digest of the full file for server-side verification. */
  expectedSha256?: string;
  /** Bounded per-chunk retry count on transient 5xx / network errors (default 3). */
  maxChunkRetries?: number;
  /** Override chunk size in bytes (used by tests). */
  chunkSizeBytes?: number;
  /** Optional progress callback invoked after each chunk succeeds. */
  onChunkProgress?: (completedChunks: number, totalChunks: number) => void;
  /** When false, keep the server session alive on error so a later retry can resume. */
  abortSessionOnError?: boolean;
}

export async function uploadRawFileChunked(
  rawFile: File,
  rawPath: string,
  authedFetch: (input: string, init?: RequestInit) => Promise<Response>,
  signal: AbortSignal,
  opts: UploadRawFileChunkedOptions = {},
): Promise<{ uploadId: string; sizeBytes: number; sha256?: string }> {
  const chunkSizeBytes = opts.chunkSizeBytes ?? RAW_UPLOAD_CHUNK_BYTES;
  const maxChunkRetries = Math.max(1, opts.maxChunkRetries ?? 3);
  const totalBytes = rawFile.size;
  const totalChunks = Math.max(1, Math.ceil(totalBytes / chunkSizeBytes));

  const post = async (payload: Record<string, unknown>): Promise<Response> =>
    authedFetch('/api/storage/upload-file-chunked', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      signal,
    });

  // 1) init — open or resume the server-side chunked upload session.
  const initRes = await post({
    phase: 'init',
    path: rawPath,
    contentType: rawFile.type || 'application/octet-stream',
    totalBytes,
    totalChunks,
    ...(opts.uploadId ? { uploadId: opts.uploadId } : {}),
    ...(opts.expectedSha256 ? { expectedSha256: opts.expectedSha256 } : {}),
  });
  if (!initRes.ok) {
    const body = await initRes.text().catch(() => '');
    throw new Error(
      `[saveDocument] chunked upload init failed (${initRes.status}): ${body.slice(0, 500)}`,
    );
  }
  const initJson = (await initRes.json().catch(() => ({}))) as {
    uploadId?: string;
    receivedChunks?: number[];
    finalized?: boolean;
    sha256?: string;
  };
  const uploadId = initJson.uploadId;
  if (!uploadId) {
    throw new Error('[saveDocument] chunked upload init returned no uploadId');
  }
  if (initJson.finalized) {
    if (
      opts.expectedSha256 &&
      initJson.sha256 &&
      initJson.sha256.toLowerCase() !== opts.expectedSha256.toLowerCase()
    ) {
      throw new Error(
        `[saveDocument] chunked upload SHA-256 mismatch (expected ${opts.expectedSha256}, got ${initJson.sha256})`,
      );
    }
    opts.onChunkProgress?.(totalChunks, totalChunks);
    return { uploadId, sizeBytes: totalBytes, sha256: initJson.sha256 ?? opts.expectedSha256 };
  }

  const receivedSet = new Set<number>(
    Array.isArray(initJson.receivedChunks) ? initJson.receivedChunks : [],
  );

  try {
    // 2) chunks — skip already-received chunks on resume; retry transient chunk errors.
    for (let index = 0; index < totalChunks; index++) {
      if (signal.aborted) {
        throw new DOMException('The operation was aborted.', 'AbortError');
      }
      if (receivedSet.has(index)) {
        opts.onChunkProgress?.(index + 1, totalChunks);
        continue;
      }
      const start = index * chunkSizeBytes;
      const end = Math.min(start + chunkSizeBytes, totalBytes);
      const slice = rawFile.slice(start, end);
      const content = await blobToBase64(slice);

      let lastErr: Error | undefined;
      for (let attempt = 1; attempt <= maxChunkRetries; attempt++) {
        if (signal.aborted) {
          throw new DOMException('The operation was aborted.', 'AbortError');
        }
        try {
          const chunkRes = await post({ phase: 'chunk', uploadId, index, content });
          if (chunkRes.ok) {
            lastErr = undefined;
            receivedSet.add(index);
            opts.onChunkProgress?.(index + 1, totalChunks);
            break;
          }
          const body = await chunkRes.text().catch(() => '');
          lastErr = new Error(
            `[saveDocument] chunked upload chunk ${index}/${totalChunks} failed (${chunkRes.status}): ${body.slice(0, 500)}`,
          );
          // Do not retry non-retryable 4xx (except 408/429).
          if (chunkRes.status >= 400 && chunkRes.status < 500 && chunkRes.status !== 408 && chunkRes.status !== 429) {
            break;
          }
        } catch (err) {
          if (
            signal.aborted ||
            (err instanceof DOMException && err.name === 'AbortError')
          ) {
            throw err;
          }
          lastErr = err instanceof Error ? err : new Error(String(err));
        }
      }
      if (lastErr) {
        throw lastErr;
      }
    }

    // 3) finalize — verify byte count + SHA-256 and flush the GCS object.
    const finRes = await post({
      phase: 'finalize',
      uploadId,
      expectedSizeBytes: totalBytes,
      ...(opts.expectedSha256 ? { expectedSha256: opts.expectedSha256 } : {}),
    });
    if (!finRes.ok) {
      const body = await finRes.text().catch(() => '');
      throw new Error(
        `[saveDocument] chunked upload finalize failed (${finRes.status}): ${body.slice(0, 500)}`,
      );
    }
    const finJson = (await finRes.json().catch(() => ({}))) as {
      size?: number;
      sizeBytes?: number;
      sha256?: string;
    };
    const verifiedSize = finJson.sizeBytes ?? finJson.size;
    if (typeof verifiedSize === 'number' && verifiedSize !== totalBytes) {
      throw new Error(
        `[saveDocument] chunked upload byte-size mismatch (expected ${totalBytes}, got ${verifiedSize})`,
      );
    }
    if (
      opts.expectedSha256 &&
      typeof finJson.sha256 === 'string' &&
      finJson.sha256.toLowerCase() !== opts.expectedSha256.toLowerCase()
    ) {
      throw new Error(
        `[saveDocument] chunked upload SHA-256 mismatch (expected ${opts.expectedSha256}, got ${finJson.sha256})`,
      );
    }
    return {
      uploadId,
      sizeBytes: verifiedSize ?? totalBytes,
      sha256: finJson.sha256 ?? opts.expectedSha256,
    };
  } catch (e) {
    // Best-effort teardown of the partial server-side session unless caller
    // requested session retention for resume.
    if (opts.abortSessionOnError !== false) {
      try {
        await authedFetch('/api/storage/upload-file-chunked', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ phase: 'abort', uploadId }),
        });
      } catch {
        /* teardown is best-effort */
      }
    }
    throw e;
  }
}

/**
 * TU1/TU2 — abort timeout for the two upload fetches in {@link saveDocument}
 * (`/api/storage/upload-file` raw-bytes POST + `/api/storage/save` GCS JSON
 * snapshot).
 */
const UPLOAD_FETCH_TIMEOUT_MS = 120_000;

/**
 * Structured warning returned by {@link saveDocument} when the metadata
 * write succeeds but the raw-file upload did not.
 */
export interface RawUploadWarning {
  kind: 'too-large' | 'upload-failed';
  /** User-facing message safe to surface in a toast. */
  message: string;
  /** Effective base64-inflated body cap that gated a `too-large` warning. */
  capBytes?: number;
  /** Raw file size in bytes (for `too-large`). */
  sizeBytes?: number;
}

/**
 * Result of {@link saveDocument}.
 */
export interface SaveDocumentResult {
  rawUploadWarning?: RawUploadWarning;
  rawBytesVerified?: boolean;
  rawStorageSizeBytes?: number;
  rawStorageSha256?: string;
}

export interface SaveDocumentOptions {
  /**
   * When true, require durable original-byte retention and byte/SHA-256 verification
   * before persisting metadata or claiming upload success. Extracted text is never
   * mistaken for a preserved original when this flag is enabled.
   */
  requireDurableOriginalBytes?: boolean;
  /** Optional deterministic/resumable session ID for chunked uploads. */
  uploadId?: string;
  /** Optional caller AbortSignal for cancellation. */
  signal?: AbortSignal;
  /** Optional per-chunk progress callback. */
  onChunkProgress?: (completedChunks: number, totalChunks: number) => void;
  /** When false, retain chunked session on error so retry can resume from received chunks. */
  abortSessionOnError?: boolean;
}

export async function saveDocument(
  siteDoc: SiteDocument,
  rawFile?: File,
  siteId?: string,
  opts?: SaveDocumentOptions,
): Promise<SaveDocumentResult> {
  if (opts?.signal?.aborted) {
    throw new DOMException('The operation was aborted.', 'AbortError');
  }
  if (siteId) {
    const useStore = await loadAppStore();
    await useStore.getState().assertSiteWritable(siteId);
  }

  const { auth, db } = await import('../../firebase');
  const user = auth.currentUser;
  if (!user) throw new Error('Not signed in — cannot save document');

  // DLB-3 — ghost-reappearance guard.
  const tomb = await checkDocTombstone(siteDoc.id);
  if (tomb.blocked) {
    throw new GhostReingestBlocked({
      docId: siteDoc.id,
      deletedAt: tomb.deletedAt,
      reason: tomb.reason,
    });
  }

  const { doc: firestoreDoc, setDoc } = await import('firebase/firestore');
  const { authedFetch, createFetchAbortTimeout } = await import('../../lib/authedFetch');
  const storagePath = `users/${user.uid}/documents/${siteDoc.id}.json`;
  siteDoc.storagePath = storagePath;

  let rawUploadWarning: RawUploadWarning | undefined;
  let rawBytesVerified = false;
  let rawStorageSizeBytes: number | undefined;
  let rawStorageSha256: string | undefined;

  if (rawFile) {
    const expectedSha256 = siteDoc.contentHash ?? (await computeFileHash(rawFile));
    if (expectedSha256 && !siteDoc.contentHash) {
      siteDoc.contentHash = expectedSha256;
    }
    if (rawFile.size > RAW_UPLOAD_MAX_BYTES) {
      const fileMb = (rawFile.size / (1024 * 1024)).toFixed(0);
      const capMb = (RAW_UPLOAD_MAX_BYTES / (1024 * 1024)).toFixed(0);
      rawUploadWarning = {
        kind: 'too-large',
        message:
          `Preview/download unavailable — "${rawFile.name}" (${fileMb} MB) exceeds the ` +
          `${capMb} MB raw-file limit. The document text was still saved.`,
        capBytes: RAW_UPLOAD_MAX_BYTES,
        sizeBytes: rawFile.size,
      };
      console.warn(
        `[saveDocument] raw file ${rawFile.name} (${rawFile.size} bytes) exceeds ` +
          `RAW_UPLOAD_MAX_BYTES (${RAW_UPLOAD_MAX_BYTES}); skipping raw upload.`,
      );
      if (opts?.requireDurableOriginalBytes) {
        throw new Error(
          `Original file "${rawFile.name}" (${fileMb} MB) exceeds the ${capMb} MB raw-storage limit; refusing to claim upload success without durable original bytes.`,
        );
      }
    } else {
      const { signal: timeoutSignal, cancel } = createFetchAbortTimeout(UPLOAD_FETCH_TIMEOUT_MS);
      const combinedController = new AbortController();
      const onTimeoutAbort = () => combinedController.abort(timeoutSignal.reason);
      const onCallerAbort = () => combinedController.abort(opts?.signal?.reason);
      timeoutSignal.addEventListener('abort', onTimeoutAbort, { once: true });
      if (opts?.signal) {
        if (opts.signal.aborted) {
          cancel();
          timeoutSignal.removeEventListener('abort', onTimeoutAbort);
          throw new DOMException('The operation was aborted.', 'AbortError');
        }
        opts.signal.addEventListener('abort', onCallerAbort, { once: true });
      }
      const signal = opts?.signal ? combinedController.signal : timeoutSignal;
      try {
        const rawPath = `users/${user.uid}/documents/${siteDoc.id}/raw/${rawFile.name}`;
        if (rawFile.size > CHUNKED_UPLOAD_THRESHOLD_BYTES) {
          const chunkResult = await uploadRawFileChunked(rawFile, rawPath, authedFetch, signal, {
            uploadId: opts?.uploadId ?? `chunk-${user.uid}-${siteDoc.id}`,
            expectedSha256,
            onChunkProgress: opts?.onChunkProgress,
            abortSessionOnError: opts?.abortSessionOnError,
          });
          rawBytesVerified = true;
          rawStorageSizeBytes = chunkResult.sizeBytes;
          rawStorageSha256 = chunkResult.sha256 ?? expectedSha256;
        } else {
          const reader = new FileReader();
          const base64 = await new Promise<string>((resolve, reject) => {
            reader.onload = () => resolve((reader.result as string).split(',')[1]);
            reader.onerror = () => reject(new Error('Failed to read file'));
            reader.readAsDataURL(rawFile);
          });
          const uploadRes = await authedFetch('/api/storage/upload-file', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              path: rawPath,
              content: base64,
              contentType: rawFile.type,
              expectedSizeBytes: rawFile.size,
              ...(expectedSha256 ? { expectedSha256 } : {}),
            }),
            signal,
          });
          if (!uploadRes.ok) {
            const body = await uploadRes.text().catch(() => '');
            throw new Error(
              `[saveDocument] /api/storage/upload-file failed (${uploadRes.status}): ${body.slice(0, 500)}`,
            );
          }
          const uploadJson = (await uploadRes.json().catch(() => ({}))) as {
            size?: number;
            sizeBytes?: number;
            sha256?: string;
          };
          const verifiedSize = uploadJson.sizeBytes ?? uploadJson.size;
          if (typeof verifiedSize === 'number' && verifiedSize !== rawFile.size) {
            throw new Error(
              `[saveDocument] /api/storage/upload-file byte-size mismatch (expected ${rawFile.size}, got ${verifiedSize})`,
            );
          }
          if (
            expectedSha256 &&
            typeof uploadJson.sha256 === 'string' &&
            uploadJson.sha256.toLowerCase() !== expectedSha256.toLowerCase()
          ) {
            throw new Error(
              `[saveDocument] /api/storage/upload-file SHA-256 mismatch (expected ${expectedSha256}, got ${uploadJson.sha256})`,
            );
          }
          rawBytesVerified = true;
          rawStorageSizeBytes = verifiedSize ?? rawFile.size;
          rawStorageSha256 = uploadJson.sha256 ?? expectedSha256;
        }
        siteDoc.rawStoragePath = rawPath;
        siteDoc.rawBytesVerified = true;
        siteDoc.rawStorageSizeBytes = rawStorageSizeBytes;
        if (rawStorageSha256) siteDoc.rawStorageSha256 = rawStorageSha256;
      } catch (e) {
        if (opts?.signal?.aborted) {
          throw new DOMException('The operation was aborted.', 'AbortError');
        }
        const timedOut =
          (e instanceof DOMException && e.name === 'AbortError') ||
          (e instanceof Error && /abort/i.test(e.message) && signal.aborted);
        if (opts?.requireDurableOriginalBytes) {
          const detail = e instanceof Error ? e.message : String(e);
          throw new Error(
            timedOut
              ? `[saveDocument] Raw original-byte storage timed out for "${rawFile.name}" — refusing to save extracted text without verified original bytes (${detail})`
              : `[saveDocument] Raw original-byte storage failed for "${rawFile.name}" — refusing to save extracted text without verified original bytes (${detail})`,
          );
        }
        rawUploadWarning = {
          kind: 'upload-failed',
          message: timedOut
            ? 'Preview/download unavailable — raw file upload timed out. The document text was still saved; re-upload the file to enable preview/download.'
            : 'Preview/download unavailable — raw file failed to save. The document text was still saved.',
        };
        console.warn(
          `[saveDocument] raw-file upload ${timedOut ? 'timed out' : 'failed'} for ${rawFile.name} (non-fatal; metadata still saved):`,
          (e as Error)?.message ?? e,
        );
      } finally {
        cancel();
        timeoutSignal.removeEventListener('abort', onTimeoutAbort);
        if (opts?.signal) opts.signal.removeEventListener('abort', onCallerAbort);
      }
    }
  } else if (opts?.requireDurableOriginalBytes) {
    throw new Error(
      `[saveDocument] Missing rawFile for "${siteDoc.fileName}" — durable original-byte retention is required before claiming upload success.`,
    );
  }

  if (!siteDoc.preview && isOfficePreviewCandidate(siteDoc.fileName, siteDoc.mimeType)) {
    siteDoc.preview = { status: 'none', updatedAt: new Date().toISOString() };
  }

  const gcsPayload: SiteDocument = {
    ...siteDoc,
    textContent: siteDoc.fullExtractedText ?? siteDoc.textContent,
  };
  const { signal, cancel } = createFetchAbortTimeout(UPLOAD_FETCH_TIMEOUT_MS);
  let saveRes: Response;
  try {
    saveRes = await authedFetch('/api/storage/save', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ path: storagePath, data: gcsPayload }),
      signal,
    });
  } catch (e) {
    const timedOut =
      (e instanceof DOMException && e.name === 'AbortError') ||
      (e instanceof Error && /abort/i.test(e.message) && signal.aborted);
    if (timedOut) {
      throw new Error(
        `[saveDocument] /api/storage/save timed out after ${UPLOAD_FETCH_TIMEOUT_MS / 1000}s — upload timed out, retry`,
      );
    }
    throw e;
  } finally {
    cancel();
  }
  if (!saveRes.ok) {
    const body = await saveRes.text().catch(() => '');
    throw new Error(`[saveDocument] /api/storage/save failed (${saveRes.status}): ${body.slice(0, 500)}`);
  }

  const payload: Record<string, unknown> = {
    id: siteDoc.id,
    fileName: siteDoc.fileName,
    mimeType: siteDoc.mimeType,
    sizeBytes: siteDoc.sizeBytes,
    summary: siteDoc.summary,
    tags: siteDoc.tags,
    uploadedAt: siteDoc.uploadedAt,
    storagePath,
  };
  if (siteDoc.relevance) payload.relevance = siteDoc.relevance;
  if (siteDoc.sedSectionsHelpful) payload.sedSectionsHelpful = siteDoc.sedSectionsHelpful;
  if (siteDoc.relevanceUpdatedAt) payload.relevanceUpdatedAt = siteDoc.relevanceUpdatedAt;
  if (siteDoc.relevanceModelVersion) payload.relevanceModelVersion = siteDoc.relevanceModelVersion;
  if (siteDoc.rawStoragePath) payload.rawStoragePath = siteDoc.rawStoragePath;
  if (siteDoc.rawBytesVerified) payload.rawBytesVerified = true;
  if (typeof siteDoc.rawStorageSizeBytes === 'number') payload.rawStorageSizeBytes = siteDoc.rawStorageSizeBytes;
  if (siteDoc.rawStorageSha256) payload.rawStorageSha256 = siteDoc.rawStorageSha256;
  if (siteDoc.enrichmentStatus) payload.enrichmentStatus = siteDoc.enrichmentStatus;
  if (siteDoc.enrichmentDeferredStages?.length) payload.enrichmentDeferredStages = siteDoc.enrichmentDeferredStages;
  if (siteDoc.preview) payload.preview = siteDoc.preview;
  const { hashBodyText } = await import('../../lib/rag/embeddingsVersion');
  const bodyForHash = siteDoc.fullExtractedText ?? siteDoc.textContent ?? '';
  if (bodyForHash.trim()) {
    payload.bodyTextHash = await hashBodyText(bodyForHash);
    payload.embeddingsReady = false;
  }
  if (siteDoc.contentHash) payload.contentHash = siteDoc.contentHash;
  if (typeof siteDoc.extractedCharsTotal === 'number') payload.extractedCharsTotal = siteDoc.extractedCharsTotal;
  if (typeof siteDoc.extractedCharsKept === 'number') payload.extractedCharsKept = siteDoc.extractedCharsKept;
  if (siteDoc.truncated) payload.truncated = true;
  if (siteDoc.pdfCoverage) payload.pdfCoverage = siteDoc.pdfCoverage;
  const { withSemaphore } = await import('../../lib/firestore/writeLimiter');
  await withSemaphore(() =>
    setDoc(firestoreDoc(db, `users/${user.uid}/documents`, siteDoc.id), payload),
  );

  // R-131-RAG-B — page-aware chunking at upload time (fire-and-forget).
  const { enqueuePersistDocumentChunks } = await import('../../lib/rag/chunkStore');
  enqueuePersistDocumentChunks(siteDoc);

  return {
    rawUploadWarning,
    ...(rawBytesVerified ? { rawBytesVerified, rawStorageSizeBytes, rawStorageSha256 } : {}),
  };
}

/**
 * Load all user documents from Firestore.
 */
export async function loadUserDocuments(options: { strict?: boolean } = {}): Promise<SiteDocument[]> {
  try {
    const { auth, db } = await import('../../firebase');
    const user = auth.currentUser;
    if (!user) {
      if (options.strict) throw new Error('Sign in before opening saved files.');
      return [];
    }

    const { collection, getDocs, query, orderBy } = await import('firebase/firestore');
    const q = query(collection(db, `users/${user.uid}/documents`), orderBy('uploadedAt', 'desc'));
    const snap = await getDocs(q);
    return snap.docs.map(d => d.data() as SiteDocument);
  } catch (error) {
    if (options.strict) throw error;
    return [];
  }
}

/**
 * Best-effort orphan-log writer. When a Storage delete fails (e.g. the doc
 * pre-dates `storagePath` capture or the bucket has temporary auth issues),
 * the user-visible delete still succeeds and we drop a row at
 * `users/{uid}/orphanStorageDeletes/{docId}` so a future background sweep
 * (DDL-FOLLOWUP-STORAGE-ORPHAN-SWEEP) can finish the cleanup.
 *
 * Never throws. Failures here would defeat the "best-effort" guarantee.
 */
async function logStorageOrphan(
  uid: string,
  docId: string,
  paths: string[],
  reason: string,
): Promise<void> {
  try {
    const { db } = await import('../../firebase');
    const { doc, setDoc } = await import('firebase/firestore');
    await setDoc(doc(db, `users/${uid}/orphanStorageDeletes`, docId), {
      docId,
      paths,
      reason,
      loggedAt: new Date().toISOString(),
    });
  } catch (e) {
    console.warn('[deleteDocument] logStorageOrphan failed (non-fatal):', (e as Error)?.message);
  }
}

/**
 * Delete a document.
 *
 * DDL-2 — Storage cleanup. Pre-DDL this routine deleted only the Firestore
 * index doc (`users/{uid}/documents/{docId}`); the actual GCS bytes at
 * `storagePath` (JSON payload) and `rawStoragePath` (raw upload) were
 * orphaned every time. Now: read the index doc, capture both paths, fire
 * best-effort `/api/storage/delete` calls, then drop the index doc.
 *
 * Storage failures DO NOT block the index delete — the user-visible row
 * disappears either way; we log an orphan-sweep entry for later cleanup.
 */
export async function deleteDocument(docId: string, siteId?: string): Promise<void> {
  // #82 — deleting a document mutates the owning site's library, so it's gated
  // by that site's edit-lock. Callers that know the active site pass `siteId`;
  // when omitted (legacy paths) the guard is skipped for back-compat. Runs at
  // ENTRY, before any Firestore / Storage delete, so a locked-site throw
  // rejects the whole delete. Outside the catch-all try below so the
  // SiteLockedError propagates rather than being logged-and-swallowed.
  if (siteId) {
    const useStore = await loadAppStore();
    await useStore.getState().assertSiteWritable(siteId);
  }
  try {
    const { auth, db } = await import('../../firebase');
    const user = auth.currentUser;
    if (!user) return;

    const { doc, deleteDoc, getDoc } = await import('firebase/firestore');
    const indexRef = doc(db, `users/${user.uid}/documents`, docId);

    // 1. Read the index doc to discover the GCS paths before deleting it.
    //    Both fields are optional on legacy uploads — those rows simply skip
    //    the Storage delete and the index row still goes away.
    let storagePath: string | undefined;
    let rawStoragePath: string | undefined;
    try {
      const snap = await getDoc(indexRef);
      if (snap.exists()) {
        const data = snap.data() as { storagePath?: string; rawStoragePath?: string };
        storagePath = data.storagePath;
        rawStoragePath = data.rawStoragePath;
      }
    } catch (e) {
      console.warn(`[deleteDocument] could not read index for ${docId}:`, (e as Error)?.message);
    }

    // 2. Best-effort Storage cleanup. Use the same `/api/storage/delete`
    //    endpoint deleteSite uses so auth/rules pass identically.
    const failedPaths: string[] = [];
    const pathsToDelete = [storagePath, rawStoragePath].filter((p): p is string => !!p);
    if (pathsToDelete.length > 0) {
      const { authedFetch } = await import('../../lib/authedFetch');
      for (const path of pathsToDelete) {
        try {
          const res = await authedFetch('/api/storage/delete', {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ path }),
          });
          if (!res.ok) {
            failedPaths.push(path);
            console.warn(`[deleteDocument] storage delete failed for ${path} (status ${res.status})`);
          }
        } catch (e) {
          failedPaths.push(path);
          console.warn(`[deleteDocument] storage delete threw for ${path}:`, (e as Error)?.message);
        }
      }
    }

    // 3. Delete chunk subcollection (R-131-RAG-B) before index doc.
    const { deleteDocumentChunks } = await import('../../lib/rag/chunkStore');
    await deleteDocumentChunks(user.uid, docId);

    // 4. Delete the Firestore index doc (existing behaviour) regardless of
    //    Storage outcome — orphans, if any, are queued for the sweep job.
    await deleteDoc(indexRef);

    if (failedPaths.length > 0) {
      await logStorageOrphan(user.uid, docId, failedPaths, 'storage-delete-failed');
    }
  } catch (e) {
    console.error('Failed to delete document:', e);
  }
}

/**
 * Format uploaded documents as context for Gemini prompts.
 * Filters by tag relevance to the site's knowledge context.
 *
 * Documents_On_Site Prompt 3 — when a `library-index`-tagged doc is
 * present, its markdown content is prepended verbatim under a
 * `--- LIBRARY MAP ---` banner so the AI sees a structured directory
 * of what's available before it reads individual document bodies.
 * The map itself is excluded from the per-document section that
 * follows (it would otherwise double-render its own contents).
 */
export function formatUploadedDocContext(documents: SiteDocument[], tags: string[]): string {
  if (documents.length === 0) return '';

  // Carve the auto-generated library map out of the doc list so it
  // isn't double-rendered and can be promoted to its own header block.
  const libraryIndex = documents.find(d => (d.tags ?? []).includes('library-index') || d.id.startsWith('library-index-'));
  const docs = libraryIndex ? documents.filter(d => d !== libraryIndex) : documents;

  if (docs.length === 0 && !libraryIndex) return '';

  // Include ALL documents — priority docs first with more content, then others with summaries
  // Budget: 30K total for document context in the Gemini prompt
  const BUDGET = 30000;
  const priority = docs.filter(d => d.priority);
  const normal = docs.filter(d => !d.priority);
  // Sort normal docs: tagged matches first, then by size
  const tagSet = new Set(tags);
  const scored = normal.sort((a, b) => {
    const aMatch = (a.tags || []).some(t => tagSet.has(t)) ? 1 : 0;
    const bMatch = (b.tags || []).some(t => tagSet.has(t)) ? 1 : 0;
    return bMatch - aMatch;
  });

  let used = 0;
  const lines: string[] = [];

  if (libraryIndex && libraryIndex.textContent) {
    // Cap the map at 8K so it never starves the per-doc section. The
    // map is information-dense (one bullet per doc) so 8K covers a
    // library well into the hundreds before truncation kicks in.
    const mapBody = wrapUntrusted(
      libraryIndex.textContent.slice(0, 8000),
      'library-index',
    );
    const mapBlock = `\n\n--- LIBRARY MAP ---\n${mapBody}`;
    lines.push(mapBlock);
    used += mapBlock.length;
  }

  // MV5: cite the laundered title in prose; the raw filename is
  // listed alongside as `(source: …)` for traceability only.
  lines.push('\n\nCONTEXT FROM USER-UPLOADED DOCUMENTS — USE these to inform analysis, cite by the **laundered title** in quotes (NOT the (source: …) filename):');

  for (const d of priority) {
    const content = (d.textContent || '').slice(0, 4000);
    const label = cleanDocFileName(d.fileName);
    const summaryWrapped = wrapUntrusted(d.summary || 'No summary', `${d.fileName}:summary`);
    const bodyWrapped = content
      ? wrapUntrusted(content, d.fileName)
      : '(no text)';
    const line = `\n★ PRIORITY "${label}" (source: ${d.fileName}) [${(d.tags || []).join(', ')}]: ${summaryWrapped}\n  ${bodyWrapped}`;
    if (used + line.length > BUDGET) break;
    lines.push(line);
    used += line.length;
  }

  // Normal docs — summaries + truncated content
  const perDocCap = Math.max(200, Math.floor((BUDGET - used) / Math.max(1, scored.length)));
  for (const d of scored) {
    const content = (d.textContent || '').slice(0, Math.min(perDocCap, 1000));
    const label = cleanDocFileName(d.fileName);
    const summaryWrapped = wrapUntrusted(d.summary || 'No summary', `${d.fileName}:summary`);
    const bodySuffix = content && !content.startsWith('[')
      ? `\n  ${wrapUntrusted(content, d.fileName)}`
      : '';
    const line = `- "${label}" (source: ${d.fileName}) [${(d.tags || []).join(', ')}]: ${summaryWrapped}${bodySuffix}`;
    if (used + line.length > BUDGET) {
      lines.push(`\n[... ${scored.length - scored.indexOf(d)} more documents — summaries only]`);
      // Add remaining as summaries only
      for (const remaining of scored.slice(scored.indexOf(d))) {
        const summaryLine = `- "${cleanDocFileName(remaining.fileName)}" (source: ${remaining.fileName}): ${remaining.summary || remaining.fileName}`;
        if (used + summaryLine.length > BUDGET + 5000) break; // 5K overflow for summaries
        lines.push(summaryLine);
        used += summaryLine.length;
      }
      break;
    }
    lines.push(line);
    used += line.length;
  }

  return lines.join('\n');
}
